import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useCRM } from '../context/CRMContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import {
  onSnapshot, doc, collection, query, orderBy, where,
  addDoc, deleteDoc, updateDoc, serverTimestamp, setDoc,
} from 'firebase/firestore';

const FIELD_TYPE_DEFS = [
  { type: 'text',     icon: '✏️',  label: 'Text',     desc: 'Single-line text input (names, labels, short answers)' },
  { type: 'number',   icon: '🔢',  label: 'Number',   desc: 'Numeric value only (scores, budgets, ages)' },
  { type: 'select',   icon: '📋',  label: 'Dropdown', desc: 'Pick one option from a list (you define the options)' },
  { type: 'date',     icon: '📅',  label: 'Date',     desc: 'Date picker — calendar selector for dates' },
  { type: 'checkbox', icon: '☑️',  label: 'Checkbox', desc: 'True / False toggle (yes / no confirmation)' },
  { type: 'phone',    icon: '📱',  label: 'Phone',    desc: 'Phone number input with validation' },
  { type: 'email',    icon: '📧',  label: 'Email',    desc: 'Email address input with format validation' },
  { type: 'url',      icon: '🔗',  label: 'URL / Link', desc: 'Website link or external URL' },
  { type: 'textarea', icon: '📝',  label: 'Long Text', desc: 'Multi-line text area for notes or detailed answers' },
];
const FIELD_TYPES = FIELD_TYPE_DEFS.map(f => f.type);
const STAGE_COLORS = ['#7c3aed','#4f46e5','#3b82f6','#06b6d4','#10b981','#34d399','#f59e0b','#ef4444','#ec4899','#f472b6'];

// ─── Default built-in sources (cannot be deleted) ─────────────────────────────
const DEFAULT_SOURCES = [
  { value: 'facebook',  label: 'Facebook',   icon: '📘', builtIn: true },
  { value: 'instagram', label: 'Instagram',  icon: '📸', builtIn: true },
  { value: 'whatsapp',  label: 'WhatsApp',   icon: '💬', builtIn: true },
  { value: 'website',   label: 'Website',    icon: '🌐', builtIn: true },
  { value: 'google',    label: 'Google Ads', icon: '🔍', builtIn: true },
  { value: 'linkedin',  label: 'LinkedIn',   icon: '💼', builtIn: true },
  { value: 'youtube',   label: 'YouTube',    icon: '▶️',  builtIn: true },
  { value: 'email',     label: 'Email',      icon: '📧', builtIn: true },
  { value: 'organic',   label: 'Organic',    icon: '🌿', builtIn: true },
  { value: 'referral',  label: 'Referral',   icon: '🤝', builtIn: true },
  { value: 'walkin',    label: 'Walk-in',    icon: '🚶', builtIn: true },
  { value: 'event',     label: 'Event',      icon: '🎪', builtIn: true },
  { value: 'agent',     label: 'Agent',      icon: '👤', builtIn: true },
  { value: 'manual',    label: 'Manual',     icon: '✍️',  builtIn: true },
];

const EMOJI_OPTIONS = ['📘','📸','💬','🌐','🔍','💼','▶️','📧','🌿','🤝','🚶','🎪','👤','✍️','🏢','📱','📞','💡','🎯','🚀','💰','🎓','🏥','🏪','🔗','📢','🎁','📊','🤖','🌍'];

function SourcesManager({ customSources, saveCustomSources }) {
  const allSources = [...DEFAULT_SOURCES, ...customSources];
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Add source form
  const [form, setForm] = useState({ label: '', icon: '🎯', parentValue: '' });
  const [addError, setAddError] = useState('');
  // Edit inline
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const handleAdd = async () => {
    setAddError('');
    const label = form.label.trim();
    if (!label) { setAddError('Name is required.'); return; }
    const value = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (allSources.find(s => s.value === value)) { setAddError('A source with that name already exists.'); return; }
    const newSource = { value, label, icon: form.icon, parentValue: form.parentValue || null, builtIn: false };
    setSaving(true);
    await saveCustomSources([...customSources, newSource]);
    setForm({ label: '', icon: '🎯', parentValue: '' });
    setSaving(false);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const handleDelete = async (value) => {
    if (!window.confirm('Remove this source? Existing leads with this source will keep their value but won\'t appear in source filters.')) return;
    await saveCustomSources(customSources.filter(s => s.value !== value));
  };

  const handleEditSave = async (value) => {
    const updated = customSources.map(s => s.value === value ? { ...s, ...editForm } : s);
    setSaving(true);
    await saveCustomSources(updated);
    setEditId(null);
    setSaving(false);
  };

  // Group: default sources, custom root sources, sub-sources
  const rootSources     = allSources.filter(s => !s.parentValue);
  const getSubSources   = (parentValue) => allSources.filter(s => s.parentValue === parentValue);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Lead Sources</h2>
        <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Manage your lead source list. Add custom sources and sub-sources for finer tracking.</p>
      </div>

      {/* Add new source form */}
      <div className="glass-card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 14 }}>Add New Source</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Emoji picker */}
          <div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Icon</div>
            <select value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
              style={{ height: 38, padding: '0 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 18, background: '#fff', cursor: 'pointer', width: 70 }}>
              {EMOJI_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          {/* Name */}
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Source Name</div>
            <input className="crm-input" placeholder="e.g. Naukri Portal, Trade Fair 2026…" value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              style={{ height: 38, fontSize: 13 }} />
          </div>
          {/* Parent (sub-source) */}
          <div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Under (optional)</div>
            <select value={form.parentValue} onChange={e => setForm(f => ({ ...f, parentValue: e.target.value }))}
              style={{ height: 38, padding: '0 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', cursor: 'pointer', minWidth: 150 }}>
              <option value="">— Top-level source —</option>
              {rootSources.map(s => <option key={s.value} value={s.value}>{s.icon} {s.label}</option>)}
            </select>
          </div>
          <button onClick={handleAdd} disabled={saving || !form.label.trim()}
            style={{ height: 38, padding: '0 20px', borderRadius: 9, border: 'none', background: saving ? '#e2e8f0' : 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', flexShrink: 0 }}>
            {saving ? 'Saving…' : '+ Add Source'}
          </button>
        </div>
        {addError && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{addError}</div>}
        {saved && <div style={{ color: '#10b981', fontSize: 12, marginTop: 8 }}>✓ Source added!</div>}
      </div>

      {/* Source list */}
      <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>All Sources ({allSources.length})</div>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>— built-in sources cannot be deleted</span>
        </div>

        {rootSources.map((source, i) => {
          const subs = getSubSources(source.value);
          const isEditing = editId === source.value && !source.builtIn;
          return (
            <div key={source.value}>
              {/* Root source row */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #f8fafc',
                background: isEditing ? 'rgba(124,58,237,0.03)' : 'transparent' }}
                onMouseEnter={e => { if (!isEditing) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ fontSize: 20, marginRight: 12, flexShrink: 0 }}>
                  {isEditing ? editForm.icon : source.icon}
                </span>
                {isEditing ? (
                  <div style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={editForm.icon} onChange={e => setEditForm(f => ({ ...f, icon: e.target.value }))}
                      style={{ height: 34, padding: '0 8px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 16, width: 60 }}>
                      {EMOJI_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                    <input className="crm-input" value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                      style={{ height: 34, fontSize: 13, flex: 1, minWidth: 140 }} />
                    <button onClick={() => handleEditSave(source.value)} disabled={saving}
                      style={{ height: 34, padding: '0 14px', borderRadius: 7, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Save</button>
                    <button onClick={() => setEditId(null)}
                      style={{ height: 34, padding: '0 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{source.label}</span>
                        {source.builtIn && <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>BUILT-IN</span>}
                        {subs.length > 0 && <span style={{ fontSize: 10, color: '#7c3aed', background: 'rgba(124,58,237,0.08)', padding: '2px 7px', borderRadius: 20, fontWeight: 600 }}>{subs.length} sub</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, fontFamily: 'monospace' }}>{source.value}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {!source.builtIn && (
                        <>
                          <button onClick={() => { setEditId(source.value); setEditForm({ label: source.label, icon: source.icon }); }}
                            style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => handleDelete(source.value)}
                            style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>✕</button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Sub-sources */}
              {subs.map(sub => {
                const isEditingSub = editId === sub.value;
                return (
                  <div key={sub.value} style={{ display: 'flex', alignItems: 'center', padding: '9px 18px 9px 48px', borderBottom: '1px solid #f8fafc', background: '#fafafa' }}
                    onMouseEnter={e => { if (!isEditingSub) e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={e => { if (!isEditingSub) e.currentTarget.style.background = '#fafafa'; }}>
                    <div style={{ width: 14, borderTop: '1.5px solid #e2e8f0', marginRight: 10, flexShrink: 0 }} />
                    <span style={{ fontSize: 16, marginRight: 10, flexShrink: 0 }}>{isEditingSub ? editForm.icon : sub.icon}</span>
                    {isEditingSub ? (
                      <div style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select value={editForm.icon} onChange={e => setEditForm(f => ({ ...f, icon: e.target.value }))}
                          style={{ height: 32, padding: '0 6px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 14, width: 56 }}>
                          {EMOJI_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                        <input className="crm-input" value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                          style={{ height: 32, fontSize: 12, flex: 1, minWidth: 120 }} />
                        <button onClick={() => handleEditSave(sub.value)} disabled={saving}
                          style={{ height: 32, padding: '0 12px', borderRadius: 7, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                        <button onClick={() => setEditId(null)}
                          style={{ height: 32, padding: '0 10px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: '#334155' }}>{sub.label}</span>
                          <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8, fontFamily: 'monospace' }}>{sub.value}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => { setEditId(sub.value); setEditForm({ label: sub.label, icon: sub.icon }); }}
                            style={{ padding: '4px 9px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 11, cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => handleDelete(sub.value)}
                            style={{ padding: '4px 9px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>✕</button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── WhatsApp Settings Component ──────────────────────────────────────────────
const WA_BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Available merge tags for templates
const TEMPLATE_TAGS = [
  { label:'First Name',  tag:'{{firstName}}' },
  { label:'Last Name',   tag:'{{lastName}}' },
  { label:'Full Name',   tag:'{{fullName}}' },
  { label:'Program',     tag:'{{program}}' },
  { label:'Course',      tag:'{{course}}' },
  { label:'Phone',       tag:'{{phone}}' },
  { label:'Email',       tag:'{{email}}' },
  { label:'Counselor',   tag:'{{counselorName}}' },
  { label:'Company',     tag:'{{companyName}}' },
  { label:'Date',        tag:'{{date}}' },
];

function TagPicker({ textareaRef, value, onChange }) {
  const insertTag = (tag) => {
    const ta = textareaRef.current;
    if (!ta) { onChange(value + tag); return; }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const next = value.slice(0, start) + tag + value.slice(end);
    onChange(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + tag.length, start + tag.length);
    }, 10);
  };
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Insert Tag</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {TEMPLATE_TAGS.map(t => (
          <button key={t.tag} type="button" onClick={() => insertTag(t.tag)}
            style={{ padding: '3px 9px', borderRadius: 6, border: '1.5px solid #7c3aed33', background: '#f5f3ff', color: '#7c3aed', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.1s' }}
            title={`Insert ${t.tag}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function WhatsAppSettings({ currentUser }) {
  const [session,       setSession]       = useState(null);
  const [templates,     setTemplates]     = useState([]);
  const [newTitle,      setNewTitle]      = useState('');
  const [newBody,       setNewBody]       = useState('');
  const [editId,        setEditId]        = useState(null);
  const [editBody,      setEditBody]      = useState('');
  const [connecting,    setConnecting]    = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const newBodyRef  = useRef(null);
  const editBodyRef = useRef(null);

  const companyId = currentUser?.companyId;

  // Subscribe to this counselor's WA session
  useEffect(() => {
    if (!currentUser?.id) return;
    return onSnapshot(doc(db, 'waSessions', currentUser.id),
      snap => setSession(snap.exists() ? snap.data() : null),
      () => setSession(null)
    );
  }, [currentUser?.id]);

  // Subscribe to templates — scoped to this company
  useEffect(() => {
    if (!companyId) return;
    const q = query(collection(db, 'waTemplates'), where('companyId', '==', companyId));
    return onSnapshot(q,
      snap => {
        const tmpl = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        tmpl.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        setTemplates(tmpl);
      },
      () => setTemplates([])
    );
  }, [companyId]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${WA_BACKEND}/api/wa/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counselorId: currentUser.id }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Connect failed');
    } catch (e) { alert(e.message); }
    finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your WhatsApp? You will need to scan QR again.')) return;
    setDisconnecting(true);
    try {
      await fetch(`${WA_BACKEND}/api/wa/disconnect/${currentUser.id}`, { method: 'DELETE' });
    } catch (e) { alert(e.message); }
    finally { setDisconnecting(false); }
  };

  const handleAddTemplate = async () => {
    if (!newTitle.trim() || !newBody.trim() || !companyId) return;
    await addDoc(collection(db, 'waTemplates'), {
      title: newTitle.trim(),
      body: newBody.trim(),
      companyId,                       // ← fix: was missing, causing Firestore rule rejection
      createdBy: currentUser.id,
      createdAt: serverTimestamp(),
    });
    setNewTitle(''); setNewBody('');
  };

  const handleDeleteTemplate = async (id) => {
    await deleteDoc(doc(db, 'waTemplates', id));
  };

  const handleSaveEdit = async (id) => {
    await updateDoc(doc(db, 'waTemplates', id), { body: editBody });
    setEditId(null);
  };

  const statusColor  = session?.status === 'connected' ? '#25d366' : session?.status === 'connecting' ? '#f59e0b' : '#94a3b8';
  const statusLabel  = session?.status === 'connected' ? `✓ Connected (+${session.phone})` : session?.status === 'connecting' ? '⟳ Connecting…' : '○ Not connected';

  const taStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid rgba(0,0,0,0.12)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ maxWidth: 640 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>💬 WhatsApp Integration</h3>
      <p style={{ fontSize: 13, color: '#475569', marginBottom: 24 }}>Connect your personal WhatsApp number to chat with leads directly from the CRM.</p>

      {/* Connection Card */}
      <div className="glass-card" style={{ padding: 20, marginBottom: 24, borderLeft: `3px solid ${statusColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Your WhatsApp Session</div>
            <div style={{ fontSize: 12, color: statusColor, marginTop: 4, fontWeight: 600 }}>{statusLabel}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(!session || session.status === 'disconnected') && (
              <button onClick={handleConnect} disabled={connecting}
                style={{ background: 'linear-gradient(135deg, #25d366, #128c7e)', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {connecting ? '…' : '📱 Connect'}
              </button>
            )}
            {session?.status === 'connected' && (
              <button onClick={handleDisconnect} disabled={disconnecting}
                style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 9, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {disconnecting ? '…' : 'Disconnect'}
              </button>
            )}
          </div>
        </div>
        {session?.status === 'connecting' && session?.qrCode && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
              Open WhatsApp → ⋮ → <strong>Linked Devices</strong> → <strong>Link a Device</strong>
            </div>
            <img src={session.qrCode} alt="WhatsApp QR" style={{ width: 200, height: 200, borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)' }} />
            <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 10 }}>⟳ Scan within 60 seconds</div>
          </div>
        )}
        {session?.status === 'connected' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(37,211,102,0.06)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#0f172a' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#25d366', display: 'inline-block', flexShrink: 0 }} />
            <span>Your WhatsApp is linked. You can chat with leads from their individual profile pages.</span>
          </div>
        )}
      </div>

      {/* Quick Reply Templates */}
      <div className="glass-card" style={{ padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>⚡ Quick Reply Templates</div>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
          Saved messages inserted with one click when chatting. Use <strong>tags</strong> like <code style={{ background: '#f5f3ff', color: '#7c3aed', padding: '1px 5px', borderRadius: 4 }}>{'{{firstName}}'}</code> — they are auto-replaced with the student's real data when sent.
        </p>

        {/* Add template form */}
        <div style={{ background: 'rgba(124,58,237,0.03)', borderRadius: 12, padding: 16, marginBottom: 16, border: '1.5px solid rgba(124,58,237,0.12)' }}>
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Template name (e.g. Welcome Message)"
            className="crm-input" style={{ width: '100%', marginBottom: 10, boxSizing: 'border-box' }} />
          <TagPicker textareaRef={newBodyRef} value={newBody} onChange={setNewBody} />
          <textarea ref={newBodyRef} value={newBody} onChange={e => setNewBody(e.target.value)}
            placeholder="Message body… use tags above to personalise"
            rows={4} style={taStyle} />
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              Tags auto-fill: firstName → student's first name, program → their program, etc.
            </div>
            <button onClick={handleAddTemplate} disabled={!newTitle.trim() || !newBody.trim()}
              style={{ background: newTitle.trim() && newBody.trim() ? 'linear-gradient(135deg,#7c3aed,#5b21b6)' : '#e2e8f0', color: newTitle.trim() && newBody.trim() ? '#fff' : '#94a3b8', border: 'none', borderRadius: 9, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Save Template
            </button>
          </div>
        </div>

        {/* Template list */}
        {templates.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '24px 0' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⚡</div>
            No templates yet. Add your first one above.
          </div>
        )}
        {templates.map(t => (
          <div key={t.id} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 16, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>⚡ {t.title}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {editId !== t.id && (
                  <button onClick={() => { setEditId(t.id); setEditBody(t.body); }}
                    style={{ fontSize: 11, color: '#475569', background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>Edit</button>
                )}
                <button onClick={() => handleDeleteTemplate(t.id)}
                  style={{ fontSize: 11, color: '#ef4444', background: 'rgba(239,68,68,0.06)', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
            {editId === t.id ? (
              <div>
                <TagPicker textareaRef={editBodyRef} value={editBody} onChange={setEditBody} />
                <textarea ref={editBodyRef} value={editBody} onChange={e => setEditBody(e.target.value)} rows={4}
                  style={{ ...taStyle, border: '1.5px solid #7c3aed', marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleSaveEdit(t.id)} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                  <button onClick={() => setEditId(null)} style={{ background: 'rgba(0,0,0,0.05)', color: '#475569', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.65, whiteSpace: 'pre-wrap', background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
                {/* Highlight {{tags}} in preview */}
                {t.body.split(/(\{\{[^}]+\}\})/g).map((part, i) =>
                  /^\{\{/.test(part)
                    ? <span key={i} style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: 4, padding: '1px 5px', fontSize: 12, fontWeight: 700 }}>{part}</span>
                    : <span key={i}>{part}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── PRIORITIES MANAGER ───────────────────────────────────────────────────────
function PrioritiesManager() {
  const { priorities: livePriorities, savePriorities } = useCRM();
  const [items, setItems] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [disabled, setDisabled] = React.useState(false);

  React.useEffect(() => {
    if (items === null && livePriorities) {
      setItems(JSON.parse(JSON.stringify(livePriorities)));
      setDisabled(livePriorities.length === 0);
    }
  }, [livePriorities]); // eslint-disable-line

  const list = items || livePriorities || [];

  const update = (idx, patch) => setItems(arr => arr.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const remove = (idx) => setItems(arr => arr.filter((_, i) => i !== idx));
  const add = () => setItems(arr => [...arr, { value: 'new_' + Date.now(), label: 'New Priority', emoji: '⭐', color: '#6366f1' }]);
  const moveUp = (idx) => {
    if (idx === 0) return;
    setItems(arr => { const a = [...arr]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; return a; });
  };
  const moveDown = (idx) => {
    setItems(arr => { if (idx >= arr.length-1) return arr; const a = [...arr]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; return a; });
  };

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      const toSave = disabled ? [] : list;
      await savePriorities(toSave);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const PRESET_COLORS = ['#ef4444','#f97316','#f59e0b','#10b981','#3b82f6','#6366f1','#8b5cf6','#ec4899','#14b8a6','#64748b'];

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>🔥 Priority Labels</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            Customize how lead priorities appear across the CRM. Disable to hide the priority column entirely.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: saved ? '#10b981' : 'var(--color-primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
          </button>
        </div>
      </div>

      {err && <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#b91c1c' }}>{err}</div>}

      {/* Disable toggle */}
      <div style={{ marginBottom: 20, padding: '14px 16px', background: disabled ? 'rgba(239,68,68,0.05)' : 'var(--bg-secondary)', border: '1px solid ' + (disabled ? 'rgba(239,68,68,0.2)' : 'var(--border)'), borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Disable Priorities Entirely</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Hides the priority field and column from all leads. Existing values are preserved.</div>
        </div>
        <button onClick={() => setDisabled(d => !d)}
          style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: disabled ? '#ef4444' : '#e2e8f0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
          <span style={{ position: 'absolute', top: 3, left: disabled ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
        </button>
      </div>

      {!disabled && (
        <>
          {list.map((p, idx) => (
            <div key={p.value + idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 11, marginBottom: 8 }}>
              {/* Drag handles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                <button onClick={() => moveUp(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10, padding: '1px 4px', lineHeight: 1 }}>▲</button>
                <button onClick={() => moveDown(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10, padding: '1px 4px', lineHeight: 1 }}>▼</button>
              </div>
              {/* Emoji */}
              <input value={p.emoji || ''} onChange={e => update(idx, { emoji: e.target.value })}
                style={{ width: 36, fontSize: 20, textAlign: 'center', border: '1px solid var(--border)', borderRadius: 7, padding: '3px 4px', background: 'var(--bg-secondary)', fontFamily: 'inherit' }}
                maxLength={2} />
              {/* Label */}
              <input value={p.label} onChange={e => update(idx, { label: e.target.value })}
                className="crm-input" placeholder="Label"
                style={{ flex: 1, fontSize: 13, fontWeight: 600 }} />
              {/* Color swatches */}
              <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap', maxWidth: 130 }}>
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => update(idx, { color: c })}
                    style={{ width: 20, height: 20, borderRadius: '50%', background: c, border: p.color === c ? '2.5px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer', padding: 0, flexShrink: 0 }} />
                ))}
              </div>
              {/* Preview badge */}
              <div style={{ padding: '3px 10px', borderRadius: 20, background: (p.color || '#6366f1') + '20', color: p.color || '#6366f1', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {p.emoji} {p.label}
              </div>
              {/* Delete */}
              <button onClick={() => remove(idx)}
                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18, flexShrink: 0, padding: '0 2px', lineHeight: 1 }}>×</button>
            </div>
          ))}
          <button onClick={add}
            style={{ width: '100%', padding: '11px', borderRadius: 11, border: '2px dashed var(--border)', background: 'transparent', color: 'var(--color-primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>
            + Add Priority
          </button>
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 11, color: 'var(--text-muted)' }}>
            💡 Drag with ▲▼ to reorder. Changes only take effect after clicking Save Changes.
          </div>
        </>
      )}
    </div>
  );
}


// ─── Default document categories (mirrors Documents.js) ──────────────────────
const DEFAULT_DOC_CATEGORIES = [
  { id: 'identity',  label: '🪪 Identity',       docs: ['Passport (all pages)', 'National ID Card', 'Birth Certificate'] },
  { id: 'academic',  label: '📚 Academic',        docs: ['Grade 10 Marksheet', 'Grade 12 Marksheet', 'Undergraduate Degree Certificate', 'Undergraduate Transcripts', 'Postgraduate Transcripts', 'School Leaving Certificate'] },
  { id: 'english',   label: '🌐 English Tests',   docs: ['IELTS Score Card', 'TOEFL Score Report', 'PTE Score Card', 'Duolingo English Certificate'] },
  { id: 'entrance',  label: '📝 Entrance Tests',  docs: ['GRE Score Report', 'GMAT Score Report', 'SAT Score Report', 'ACT Score Report'] },
  { id: 'sop',       label: '✍️ Essays & SOP',    docs: ['Statement of Purpose (SOP)', 'Personal Statement', 'Research Proposal', 'Motivation Letter'] },
  { id: 'lor',       label: '📩 Recommendations', docs: ['LOR – Academic 1', 'LOR – Academic 2', 'LOR – Professional'] },
  { id: 'financial', label: '💰 Financial',       docs: ['Bank Statement (6 months)', 'Scholarship Award Letter', 'Sponsorship Letter', 'Income Tax Returns', 'Salary Slips (3 months)'] },
  { id: 'work',      label: '💼 Work Experience', docs: ['Resume / CV', 'Work Experience Certificate', 'Employment Letter', 'Pay Stubs / Salary Evidence'] },
  { id: 'visa',      label: '✈️ Visa & Travel',   docs: ['Visa Application Form', 'Visa Fee Receipt', 'Travel Insurance', 'Previous Visa Copy', 'Offer Letter from University'] },
  { id: 'other',     label: '📁 Other',           docs: ['Photograph (passport size)', 'Gap Certificate', 'Medical Certificate'] },
];

// ─── DocTypesManager ──────────────────────────────────────────────────────────
function DocTypesManager() {
  const { currentUser } = useCRM();
  const companyId = currentUser?.companyId;

  const [categories, setCategories] = useState(null);
  const [saving,      setSaving]     = useState(false);
  const [saved,       setSaved]      = useState(false);
  const [saveErr,     setSaveErr]    = useState('');
  const [newCatLabel, setNewCatLabel]= useState('');
  const [addingDoc,   setAddingDoc]  = useState({});
  const [expanded,    setExpanded]   = useState({});

  useEffect(() => {
    if (!companyId) return;
    const ref = doc(db, 'companies', companyId, 'settings', 'documentTypes');
    const unsub = onSnapshot(ref, snap => {
      const data = snap.data();
      if (snap.exists() && Array.isArray(data?.categories) && data.categories.length) {
        setCategories(data.categories);
        setExpanded(prev => {
          const next = {};
          data.categories.forEach(c => { next[c.id] = prev[c.id] !== false; });
          return next;
        });
      } else {
        setCategories(DEFAULT_DOC_CATEGORIES);
        const exp = {};
        DEFAULT_DOC_CATEGORIES.forEach(c => { exp[c.id] = true; });
        setExpanded(exp);
      }
    }, () => setCategories(DEFAULT_DOC_CATEGORIES));
    return unsub;
  }, [companyId]);

  const persist = async (cats) => {
    if (!companyId) return;
    setSaving(true); setSaveErr('');
    try {
      await setDoc(doc(db, 'companies', companyId, 'settings', 'documentTypes'), { categories: cats }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setSaveErr(e.message); }
    finally { setSaving(false); }
  };

  const updLabel  = (id, val)  => setCategories(prev => prev.map(c => c.id === id ? { ...c, label: val } : c));
  const removeDoc = (id, name) => setCategories(prev => prev.map(c => c.id === id ? { ...c, docs: c.docs.filter(d => d !== name) } : c));
  const addDocItem = (id) => {
    const text = (addingDoc[id] || '').trim();
    if (!text) return;
    setCategories(prev => prev.map(c => c.id === id && !c.docs.includes(text) ? { ...c, docs: [...c.docs, text] } : c));
    setAddingDoc(a => ({ ...a, [id]: '' }));
  };
  const removeCat = (id) => {
    if (!window.confirm('Remove this category and all its document types?')) return;
    setCategories(prev => prev.filter(c => c.id !== id));
  };
  const addCat = () => {
    const label = newCatLabel.trim();
    if (!label) return;
    const id = 'cat_' + Date.now();
    setCategories(prev => [...prev, { id, label, docs: [] }]);
    setExpanded(e => ({ ...e, [id]: true }));
    setNewCatLabel('');
  };
  const restoreDefaults = () => {
    if (!window.confirm('Restore default document types? Your customisations will be lost.')) return;
    setCategories(DEFAULT_DOC_CATEGORIES);
    const exp = {};
    DEFAULT_DOC_CATEGORIES.forEach(c => { exp[c.id] = true; });
    setExpanded(exp);
  };

  if (!categories) return <div style={{ padding: 24, color: '#94a3b8', fontSize: 13 }}>Loading…</div>;

  const inp = { width: '100%', padding: '8px 11px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>Document Types</h2>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4, marginBottom: 0 }}>
            Customise the document checklist shown in the Documents module. Changes apply to all students.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, marginLeft: 16 }}>
          {saved    && <span style={{ fontSize: 12, color: '#10b981', fontWeight: 700 }}>✅ Saved</span>}
          {saveErr  && <span style={{ fontSize: 11, color: '#ef4444' }}>{saveErr}</span>}
          <button onClick={restoreDefaults}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#64748b', fontFamily: 'inherit' }}>
            Restore Defaults
          </button>
          <button onClick={() => persist(categories)} disabled={saving}
            style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: saving ? '#94a3b8' : '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {categories.map(cat => (
          <div key={cat.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: '#f8fafc', borderBottom: expanded[cat.id] ? '1px solid #e2e8f0' : 'none' }}>
              <button onClick={() => setExpanded(e => ({ ...e, [cat.id]: !e[cat.id] }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#64748b', padding: '0 2px', flexShrink: 0, fontFamily: 'inherit' }}>
                {expanded[cat.id] ? '▾' : '▸'}
              </button>
              <input value={cat.label} onChange={e => updLabel(cat.id, e.target.value)}
                style={{ flex: 1, fontWeight: 700, fontSize: 13, background: 'transparent', border: '1px solid transparent', borderRadius: 6, padding: '3px 7px', outline: 'none', fontFamily: 'inherit', color: '#0f172a' }}
                onFocus={e => { e.target.style.border = '1px solid #7c3aed'; e.target.style.background = '#fff'; }}
                onBlur={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }} />
              <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{cat.docs.length} docs</span>
              <button onClick={() => removeCat(cat.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#ef4444', padding: '0 4px', lineHeight: 1, flexShrink: 0, fontFamily: 'inherit' }}
                title="Remove category">×</button>
            </div>

            {expanded[cat.id] && (
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {cat.docs.map(d => (
                    <div key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f1f5f9', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: '#334155' }}>
                      <span>{d}</span>
                      <button onClick={() => removeDoc(cat.id, d)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, lineHeight: 1, padding: 0, fontFamily: 'inherit' }}
                        title="Remove">×</button>
                    </div>
                  ))}
                  {cat.docs.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No document types yet — add one below</span>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={addingDoc[cat.id] || ''}
                    onChange={e => setAddingDoc(a => ({ ...a, [cat.id]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && addDocItem(cat.id)}
                    placeholder="Add document type…"
                    style={{ ...inp, flex: 1, fontSize: 12, padding: '6px 10px' }} />
                  <button onClick={() => addDocItem(cat.id)}
                    style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: 'rgba(124,58,237,0.1)', color: '#7c3aed', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                    + Add
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <input value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addCat()}
          placeholder="New category label (e.g. 🎓 Scholarship)"
          style={{ ...inp, flex: 1 }} />
        <button onClick={addCat}
          style={{ padding: '8px 18px', borderRadius: 8, border: '1px dashed #7c3aed', background: 'rgba(124,58,237,0.06)', color: '#7c3aed', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
          Add Category
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const {
    users, leads, customFields, pipelineStages,
    integrations: savedIntegrations, saveIntegrations,
    addUser, deleteUser, updateCurrentUser,
    addCustomField, deleteCustomField,
    addStage, deleteStage, updateStage,
    currentUser, setCurrentPage,
    customSources, saveCustomSources,
    rrSettings, saveRRSettings,
    company, updateCompany,
    restoreDefaults, deduplicateData,
  } = useCRM();

  // Detect which OAuth provider the user used to log into the CRM.
  // If they signed in with Google or Microsoft, that account is already their
  // work account — no separate "Connect" step needed in Email & Calendar.
  const { firebaseUser } = useAuth();
  const loginGoogleInfo   = firebaseUser?.providerData?.find(p => p.providerId === 'google.com');
  const loginMicrosoftInfo = firebaseUser?.providerData?.find(p => p.providerId === 'microsoft.com');
  // Email used to log in (from provider, falling back to firebaseUser.email)
  const loginGoogleEmail    = loginGoogleInfo?.email    || (loginGoogleInfo    ? firebaseUser?.email : null);
  const loginMicrosoftEmail = loginMicrosoftInfo?.email || (loginMicrosoftInfo ? firebaseUser?.email : null);

  const [tab, setTab] = useState(() => {
    // If returning from OAuth flow, land on the Email & Calendar tab
    const p = new URLSearchParams(window.location.search);
    return p.get('connected') ? 'emailcal' : 'profile';
  });
  const [showAddUser,  setShowAddUser]  = useState(false);
  const [showAddField, setShowAddField] = useState(false);
  const [showAddStage, setShowAddStage] = useState(false);
  const [configIntg,   setConfigIntg]   = useState(null);

  const [userForm,  setUserForm]  = useState({ name: '', email: '', role: 'counsellor' });
  const [fieldForm, setFieldForm] = useState({ label: '', type: 'text', options: '', required: false });
  const [stageForm, setStageForm] = useState({ name: '', color: '#7c3aed' });
  const [editingStage, setEditingStage] = useState(null);

  // My Profile state
  const [profileForm,    setProfileForm]    = useState({ name: '', email: '' });
  const [profileSaving,  setProfileSaving]  = useState(false);
  const [profileSaved,   setProfileSaved]   = useState(false);
  const [profileError,   setProfileError]   = useState('');

  // Team invite state
  const [inviting,   setInviting]   = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Live per-member lead count. `user.leads` is a denormalized field that's
  // only ever set to 0 at member-creation time and never updated after that —
  // it was always showing "0 leads" regardless of actual assignments. Count
  // live from the leads collection by assignedToId instead.
  const leadCountByUser = useMemo(() => {
    const counts = {};
    for (const l of (leads || [])) {
      if (l.assignedToId) counts[l.assignedToId] = (counts[l.assignedToId] || 0) + 1;
    }
    return counts;
  }, [leads]);

  // Deduplication + restore state
  const [dedupRunning,   setDedupRunning]   = useState(false);
  const [dedupResult,    setDedupResult]    = useState(null);
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [restoreResult,  setRestoreResult]  = useState(null);

  // Branding state
  const [brandForm,    setBrandForm]    = useState({ appName: '', primary: '#7c3aed', secondary: '#3b82f6', logoUrl: '' });
  const [brandSaving,  setBrandSaving]  = useState(false);
  const [brandSaved,   setBrandSaved]   = useState(false);

  // Email & Calendar OAuth state
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const [oauthStatus,  setOauthStatus]  = useState({});   // { google: {...}, microsoft: {...} }
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthDisconnecting, setOauthDisconnecting] = useState('');
  const [oauthWaking, setOauthWaking] = useState(''); // provider being woken up

  // ── Documents Drive config ──────────────────────────────────────────────────
  const [driveConfig,        setDriveConfig]        = useState(null);  // company-level drive config
  const [driveConfigLoading, setDriveConfigLoading] = useState(false);
  const [driveFolderInput,   setDriveFolderInput]   = useState('');
  const [driveSaving,        setDriveSaving]        = useState(false);
  const [driveSaveMsg,       setDriveSaveMsg]        = useState('');
  const [selectedProvider,   setSelectedProvider]   = useState(''); // admin-chosen provider

  useEffect(() => {
    if (tab !== 'documents' || !currentUser?.companyId) return;
    setDriveConfigLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/drive/company-config?companyId=${currentUser.companyId}`).then(r => r.json()).catch(() => ({})),
      fetch(`${BACKEND_URL}/api/oauth/status?companyId=${currentUser.companyId}&userId=${currentUser.id}`).then(r => r.json()).catch(() => ({})),
    ]).then(([cfg, oauth]) => {
      setDriveConfig(cfg);
      setDriveFolderInput(cfg?.baseFolderUrl || '');
      setOauthStatus(oauth);
      // Pre-select: saved backend config → localStorage draft → nothing
      const lsKey = `driveProviderDraft_${currentUser.companyId}`;
      const draft  = localStorage.getItem(lsKey) || '';
      setSelectedProvider(cfg?.provider || draft);
    }).finally(() => setDriveConfigLoading(false));
  }, [tab, currentUser?.companyId, currentUser?.id]); // eslint-disable-line

  const handleSaveDriveConfig = async () => {
    if (!driveFolderInput.trim() || !currentUser?.companyId) return;
    if (!selectedProvider) { setDriveSaveMsg('❌ Please select a drive provider (Google Drive or OneDrive) above.'); return; }
    if (!oauthStatus[selectedProvider]?.connected) { setDriveSaveMsg(`❌ ${selectedProvider === 'google' ? 'Google Drive' : 'OneDrive'} is not connected. Go to Email & Calendar and connect it first.`); return; }
    setDriveSaving(true); setDriveSaveMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/drive/company-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: currentUser.companyId, adminUserId: currentUser.id, provider: selectedProvider, folderUrl: driveFolderInput.trim() }),
      });
      const data = await res.json();
      // Saved but token expired — show warning, not hard error
      if (data.saved && data.needsReconnect) {
        setDriveConfig({ configured: true, provider: selectedProvider, baseFolderUrl: driveFolderInput.trim(), baseFolderName: 'Shared Folder' });
        localStorage.removeItem(`driveProviderDraft_${currentUser.companyId}`);
        setDriveSaveMsg(`⚠️ Folder URL saved, but your Microsoft token is expired. Go to Email & Calendar → disconnect Microsoft → reconnect, then uploads will work.`);
        return;
      }
      // Saved with lazy folder resolution (SharePoint sharing links) — no hard error
      if (data.saved && data.warning) {
        setDriveConfig({ configured: true, provider: selectedProvider, baseFolderUrl: driveFolderInput.trim(), baseFolderName: 'Shared Folder' });
        localStorage.removeItem(`driveProviderDraft_${currentUser.companyId}`);
        setDriveSaveMsg(`✅ Folder URL saved! ${data.warning}`);
        return;
      }
      if (!res.ok || data.error) throw new Error(data.error);
      setDriveConfig({ configured: true, ...data });
      localStorage.removeItem(`driveProviderDraft_${currentUser.companyId}`);
      setDriveSaveMsg(`✅ Saved! Folder: "${data.baseFolderName}" (${data.provider === 'google' ? 'Google Drive' : 'OneDrive'})`);
    } catch (err) {
      setDriveSaveMsg('❌ ' + err.message);
    } finally {
      setDriveSaving(false);
    }
  };
  // Detect successful OAuth return (backend redirects here with ?connected=<provider>)
  const [oauthSuccess, setOauthSuccess] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('connected') || '';
  });

  const loadOAuthStatus = useCallback(async () => {
    if (!currentUser?.id || !currentUser?.companyId) return;
    setOauthLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/oauth/status?companyId=${encodeURIComponent(currentUser.companyId)}&userId=${encodeURIComponent(currentUser.id)}`);
      if (res.ok) setOauthStatus(await res.json());
    } catch { /* ignore — backend may not be running locally */ }
    finally { setOauthLoading(false); }
  }, [BACKEND_URL, currentUser?.id, currentUser?.companyId]);

  const handleConnect = async (provider) => {
    if (!currentUser?.id || !currentUser?.companyId) return;
    // Pass login_hint so OAuth pre-selects the same account the user signed into CRM with.
    const loginProviderEmail = firebaseUser?.providerData?.find(
      p => (provider === 'google' ? p.providerId === 'google.com' : p.providerId === 'microsoft.com')
    )?.email || firebaseUser?.email;
    const params = new URLSearchParams({
      companyId: currentUser.companyId,
      userId: currentUser.id,
      returnPage: 'settings',
      ...(loginProviderEmail ? { login_hint: loginProviderEmail } : {}),
    });
    const url = `${BACKEND_URL}/api/oauth/${provider}/start?${params.toString()}`;
    try {
      // Wake the backend before redirecting so the user sees a loading state
      // inside the CRM rather than Render's raw terminal boot screen.
      setOauthWaking(provider);
      await fetch(`${BACKEND_URL}/health`, { signal: AbortSignal.timeout(60000) });
    } catch { /* ignore — redirect regardless */ }
    finally { setOauthWaking(''); }
    window.location.href = url;
  };

  const handleDisconnect = async (provider) => {
    if (!currentUser?.id || !currentUser?.companyId) return;
    setOauthDisconnecting(provider);
    try {
      await fetch(`${BACKEND_URL}/api/oauth/${provider}/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: currentUser.companyId, userId: currentUser.id }),
      });
      await loadOAuthStatus();
    } catch { /* ignore */ }
    finally { setOauthDisconnecting(''); }
  };

  // Access control state
  const ROLES = ['admin', 'counsellor'];
  const ALL_PAGES = [
    { id: 'dashboard',    label: 'Dashboard' },
    { id: 'leads',        label: 'Leads' },
    { id: 'pipeline',     label: 'Pipeline' },
    { id: 'followups',    label: 'Follow-ups' },
    { id: 'campaigns',    label: 'Campaigns' },
    { id: 'webforms',     label: 'Web Forms' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'assessments',  label: 'Assessments' },
    { id: 'reports',      label: 'Reports' },
    { id: 'activitylog',  label: 'Activity Log' },
    { id: 'engagement',   label: 'Engagement' },
    { id: 'meetings',     label: 'Meetings' },
    { id: 'documents',    label: 'Documents' },
    { id: 'applications', label: 'Applications' },
    { id: 'universities', label: 'Universities' },
    { id: 'settings',     label: 'Settings' },
  ];
  const [accessControl, setAccessControl] = useState({});
  const [accessSaving,   setAccessSaving] = useState(false);
  const [accessSaved,    setAccessSaved]  = useState(false);

  // Sync profile form when currentUser loads
  useEffect(() => {
    if (currentUser) {
      setProfileForm({ name: currentUser.name || '', email: currentUser.email || '' });
    }
  }, [currentUser?.id]); // eslint-disable-line

  // Sync branding from company doc
  useEffect(() => {
    if (company?.theme) {
      setBrandForm({
        appName:   company.theme.appName   || '',
        primary:   company.theme.primary   || '#7c3aed',
        secondary: company.theme.secondary || '#3b82f6',
        logoUrl:   company.logoUrl || '',
      });
    }
  }, [company?.id]); // eslint-disable-line

  // Sync access control from company doc
  useEffect(() => {
    if (company?.accessControl) setAccessControl(company.accessControl);
  }, [company?.id]); // eslint-disable-line

  // Load OAuth status when emailcal tab is opened (or user resolves)
  useEffect(() => {
    if (tab === 'emailcal' && currentUser?.id) loadOAuthStatus();
  }, [tab, currentUser?.id]); // eslint-disable-line

  /* ── Handlers ─────────────────────────────────────────────────────────── */

  const handleRestore = async () => {
    setRestoreRunning(true); setRestoreResult(null);
    try {
      const result = await restoreDefaults();
      setRestoreResult(result);
      setTimeout(() => setRestoreResult(null), 5000);
    } catch {
      setRestoreResult({ error: true });
    } finally {
      setRestoreRunning(false);
    }
  };

  const handleDedup = async () => {
    setDedupRunning(true);
    setDedupResult(null);
    try {
      const result = await deduplicateData();
      setDedupResult(result);
      setTimeout(() => setDedupResult(null), 5000);
    } catch {
      setDedupResult({ error: true });
    } finally {
      setDedupRunning(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) { setProfileError('Name cannot be empty.'); return; }
    setProfileSaving(true); setProfileError('');
    try {
      await updateCurrentUser({ name: profileForm.name.trim() });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch {
      setProfileError('Save failed — please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAddUser = async () => {
    if (!userForm.name || !userForm.email) return;
    // ── User cap enforcement ────────────────────────────────────────────────
    const cap = company?.userCap;
    if (cap && typeof cap === 'number') {
      const activeCount = (users || []).filter(u => u.status !== 'removed').length;
      if (activeCount >= cap) {
        setInviteError(`User limit reached (${cap} max). Contact your super admin to increase the quota.`);
        return;
      }
    }
    setInviting(true); setInviteError('');
    try {
      await addUser(userForm);
      setUserForm({ name: '', email: '', role: 'counsellor' });
      setShowAddUser(false);
    } catch {
      setInviteError('Could not add member — please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleAddField = () => {
    if (!fieldForm.label) return;
    addCustomField({ ...fieldForm, options: fieldForm.options ? fieldForm.options.split(',').map(s => s.trim()) : [] });
    setFieldForm({ label: '', type: 'text', options: '', required: false });
    setShowAddField(false);
  };

  const handleAddStage = () => {
    if (!stageForm.name) return;
    addStage({ name: stageForm.name, color: stageForm.color });
    setStageForm({ name: '', color: '#7c3aed' });
    setShowAddStage(false);
  };

  const handleSaveStageEdit = () => {
    if (!editingStage?.name) return;
    updateStage(editingStage.id, { name: editingStage.name });
    setEditingStage(null);
  };

  const handleSaveBranding = async () => {
    setBrandSaving(true);
    try {
      await updateCompany({
        logoUrl: brandForm.logoUrl,
        theme: {
          appName:   brandForm.appName,
          primary:   brandForm.primary,
          secondary: brandForm.secondary,
        },
      });
      setBrandSaved(true);
      setTimeout(() => setBrandSaved(false), 2500);
    } catch { /* ignore */ }
    finally { setBrandSaving(false); }
  };

  const handleSaveAccess = async () => {
    setAccessSaving(true);
    try {
      await updateCompany({ accessControl });
      setAccessSaved(true);
      setTimeout(() => setAccessSaved(false), 2500);
    } catch { /* ignore */ }
    finally { setAccessSaving(false); }
  };

  const togglePageAccess = (role, pageId) => {
    setAccessControl(prev => {
      const rolePages = prev[role] || ALL_PAGES.map(p => p.id);
      const updated   = rolePages.includes(pageId)
        ? rolePages.filter(p => p !== pageId)
        : [...rolePages, pageId];
      return { ...prev, [role]: updated };
    });
  };

  const toggleUserPageAccess = (userId, pageId, currentlyEffective) => {
    setAccessControl(prev => {
      const prevUsers  = prev.users || {};
      const override   = prevUsers[userId] || { allow: [], deny: [] };
      let allow = [...(override.allow || [])];
      let deny  = [...(override.deny  || [])];
      if (currentlyEffective) {
        allow = allow.filter(p => p !== pageId);
        if (!deny.includes(pageId)) deny.push(pageId);
      } else {
        deny  = deny.filter(p => p !== pageId);
        if (!allow.includes(pageId)) allow.push(pageId);
      }
      return { ...prev, users: { ...prevUsers, [userId]: { allow, deny } } };
    });
  };

  const clearUserOverrides = (userId) => {
    setAccessControl(prev => {
      const prevUsers = { ...(prev.users || {}) };
      delete prevUsers[userId];
      return { ...prev, users: prevUsers };
    });
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  return (
    <div className="page-enter" style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* Sidebar */}
      <div style={{ width: 200, borderRight: '1px solid rgba(0,0,0,0.05)', padding: '16px 12px', flexShrink: 0 }}>
        <div className="section-label" style={{ marginBottom: 8 }}>CONFIGURE</div>
        {[
          { id: 'profile',      label: '👤 My Profile',       adminOnly: false },
          { id: 'pipeline',     label: '🗂 Pipeline Stages',  adminOnly: false },
          { id: 'fields',       label: '📝 Custom Fields',    adminOnly: false },
          { id: 'team',         label: '👥 Team Members',     adminOnly: true  },
          { id: 'integrations', label: '🔗 Integrations',     adminOnly: true  },
          { id: 'documents',    label: '📁 Documents Drive',  adminOnly: true  },
          { id: 'emailcal',     label: '📧 Email & Calendar', adminOnly: false },
          { id: 'roundrobin',   label: '🔄 Round Robin',      adminOnly: true  },
          { id: 'sources',      label: '🏷️ Sources',          adminOnly: true  },
          { id: 'priorities',   label: '🔥 Priorities',       adminOnly: true  },
          { id: 'docTypes',     label: '📑 Doc Types',        adminOnly: true  },
          { id: 'whatsapp',     label: '💬 WhatsApp',         adminOnly: false },
          { id: 'waai',         label: '🤖 WA AI Bot',        adminOnly: true  },
          { id: 'branding',     label: '🎨 Branding',         adminOnly: true  },
          { id: 'access',       label: '🔐 Access Control',   adminOnly: true  },
          { id: 'general',      label: '⚙️ General',          adminOnly: true  },
        ].filter(item => !item.adminOnly || currentUser?.role === 'admin' || currentUser?.role === 'superadmin')
        .map(item => (
          <div key={item.id} className={`nav-item${tab === item.id ? ' active' : ''}`}
            onClick={() => setTab(item.id)} style={{ fontSize: 13, marginBottom: 2 }}>
            {item.label}
          </div>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

        {/* ── My Profile ──────────────────────────────────────────────── */}
        {tab === 'profile' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>My Profile</h2>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Manage your account name and details</p>
            </div>

            {/* Avatar card */}
            <div className="glass-card" style={{ padding: 24, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 20 }}>
              <div className="avatar" style={{
                background: currentUser.avatar?.bg || '#7c3aed',
                color: currentUser.avatar?.color || '#fff',
                width: 64, height: 64, fontSize: 22, fontWeight: 700, flexShrink: 0,
              }}>
                {(currentUser.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{currentUser.name}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{currentUser.email}</div>
                <span style={{
                  display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  padding: '3px 10px', borderRadius: 20,
                  background: currentUser.role === 'admin' ? 'rgba(239,68,68,0.12)' : 'rgba(124,58,237,0.12)',
                  color: currentUser.role === 'admin' ? '#ef4444' : '#7c3aed',
                }}>{currentUser.role}</span>
              </div>
            </div>

            {/* Edit form */}
            <div className="glass-card" style={{ padding: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 18 }}>Edit Details</div>
              <div style={{ display: 'grid', gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Display Name *</label>
                  <input
                    className="crm-input"
                    value={profileForm.name}
                    onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Your full name"
                    style={{ fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Email Address</label>
                  <input
                    className="crm-input"
                    value={profileForm.email}
                    disabled
                    style={{ fontSize: 14, opacity: 0.6, cursor: 'not-allowed', background: 'rgba(0,0,0,0.04)' }}
                  />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Email cannot be changed here.</div>
                </div>
              </div>

              {profileError && (
                <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 14 }}>
                  ⚠️ {profileError}
                </div>
              )}
              {profileSaved && (
                <div style={{ padding: '10px 14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, fontSize: 13, color: '#10b981', marginBottom: 14 }}>
                  ✓ Profile saved successfully
                </div>
              )}

              <button className="btn-primary" onClick={handleSaveProfile} disabled={profileSaving} style={{ fontSize: 13, padding: '10px 20px' }}>
                {profileSaving ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="animate-spin"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                    Saving…
                  </span>
                ) : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* ── Pipeline Stages ─────────────────────────────────────────── */}
        {tab === 'pipeline' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Pipeline Stages</h2>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Customize your lead journey stages</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleRestore} disabled={restoreRunning}
                  style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(16,185,129,0.35)', background: 'rgba(16,185,129,0.08)', color: '#059669', cursor: 'pointer', fontWeight: 500 }}>
                  {restoreRunning ? '⏳ Restoring…' : '♻️ Restore Defaults'}
                </button>
                <button onClick={handleDedup} disabled={dedupRunning}
                  style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', color: '#d97706', cursor: 'pointer', fontWeight: 500 }}>
                  {dedupRunning ? '⏳ Fixing…' : '🧹 Fix Duplicates'}
                </button>
                <button className="btn-primary" onClick={() => setShowAddStage(true)} style={{ fontSize: 13, padding: '8px 16px' }}>+ Add Stage</button>
              </div>
            </div>
            {restoreResult && !restoreResult.error && (
              <div style={{ padding: '10px 14px', borderRadius: 9, marginBottom: 10, fontSize: 13, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#059669' }}>
                {restoreResult.stagesAdded + restoreResult.fieldsAdded > 0
                  ? `✓ Restored ${restoreResult.stagesAdded} stage(s) and ${restoreResult.fieldsAdded} field(s).`
                  : '✓ All defaults are already present — nothing to restore.'}
              </div>
            )}
            {dedupResult && !dedupResult.error && (
              <div style={{ padding: '10px 14px', borderRadius: 9, marginBottom: 14, fontSize: 13, background: dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0 ? 'rgba(16,185,129,0.08)' : 'rgba(100,116,139,0.08)', border: `1px solid ${dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0 ? 'rgba(16,185,129,0.2)' : 'rgba(100,116,139,0.2)'}`, color: dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0 ? '#059669' : '#475569' }}>
                {dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0
                  ? `✓ Removed ${dedupResult.stagesRemoved} duplicate stage(s) and ${dedupResult.fieldsRemoved} duplicate field(s).`
                  : '✓ No duplicates found — everything looks clean!'}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pipelineStages.map((stage, i) => (
                <div key={stage.id} style={{ padding: '14px 16px', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 6, height: 32, borderRadius: 3, background: stage.color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    {editingStage?.id === stage.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input className="crm-input" value={editingStage.name}
                          onChange={e => setEditingStage(p => ({ ...p, name: e.target.value }))}
                          style={{ height: 32, fontSize: 13, padding: '4px 10px' }} autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveStageEdit(); if (e.key === 'Escape') setEditingStage(null); }} />
                        <button onClick={handleSaveStageEdit} className="btn-primary" style={{ padding: '4px 10px', fontSize: 12 }}>✓ Save</button>
                        <button onClick={() => setEditingStage(null)} className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{stage.name}</div>
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 1 }}>Stage {i + 1}</div>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    {STAGE_COLORS.map(c => (
                      <div key={c} onClick={() => updateStage(stage.id, { color: c })}
                        style={{ width: 16, height: 16, borderRadius: '50%', background: c, cursor: 'pointer', border: stage.color === c ? '2.5px solid #374151' : '2px solid transparent', flexShrink: 0 }} />
                    ))}
                  </div>
                  <button onClick={() => setEditingStage({ id: stage.id, name: stage.name })}
                    style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                    ✏️ Rename
                  </button>
                  <button onClick={() => deleteStage(stage.id)}
                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {showAddStage && (
              <div style={{ marginTop: 16, padding: '16px', background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#a78bfa', marginBottom: 12 }}>New Stage</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Stage Name *</label>
                    <input className="crm-input" placeholder="e.g. Pre-Screening" value={stageForm.name} onChange={e => setStageForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Color</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {STAGE_COLORS.map(c => (
                        <div key={c} onClick={() => setStageForm(p => ({ ...p, color: c }))}
                          style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer', border: stageForm.color === c ? '2.5px solid #374151' : '2px solid transparent' }} />
                      ))}
                    </div>
                  </div>
                  <button className="btn-primary" onClick={handleAddStage} style={{ padding: '10px 16px' }}>Add</button>
                  <button className="btn-ghost" onClick={() => setShowAddStage(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Custom Fields ───────────────────────────────────────────── */}
        {tab === 'fields' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Custom Fields</h2>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Define what information to capture for each lead</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleRestore} disabled={restoreRunning}
                  style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(16,185,129,0.35)', background: 'rgba(16,185,129,0.08)', color: '#059669', cursor: 'pointer', fontWeight: 500 }}>
                  {restoreRunning ? '⏳ Restoring…' : '♻️ Restore Defaults'}
                </button>
                <button onClick={handleDedup} disabled={dedupRunning}
                  style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', color: '#d97706', cursor: 'pointer', fontWeight: 500 }}>
                  {dedupRunning ? '⏳ Fixing…' : '🧹 Fix Duplicates'}
                </button>
                <button className="btn-primary" onClick={() => setShowAddField(true)} style={{ fontSize: 13, padding: '8px 16px' }}>+ Add Field</button>
              </div>
            </div>
            {restoreResult && !restoreResult.error && (
              <div style={{ padding: '10px 14px', borderRadius: 9, marginBottom: 10, fontSize: 13, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#059669' }}>
                {restoreResult.stagesAdded + restoreResult.fieldsAdded > 0
                  ? `✓ Restored ${restoreResult.stagesAdded} stage(s) and ${restoreResult.fieldsAdded} field(s).`
                  : '✓ All defaults are already present — nothing to restore.'}
              </div>
            )}
            {dedupResult && !dedupResult.error && (
              <div style={{ padding: '10px 14px', borderRadius: 9, marginBottom: 14, fontSize: 13, background: dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0 ? 'rgba(16,185,129,0.08)' : 'rgba(100,116,139,0.08)', border: `1px solid ${dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0 ? 'rgba(16,185,129,0.2)' : 'rgba(100,116,139,0.2)'}`, color: dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0 ? '#059669' : '#475569' }}>
                {dedupResult.stagesRemoved + dedupResult.fieldsRemoved > 0
                  ? `✓ Removed ${dedupResult.stagesRemoved} duplicate stage(s) and ${dedupResult.fieldsRemoved} duplicate field(s).`
                  : '✓ No duplicates found — everything looks clean!'}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {customFields.map(field => (
                <div key={field.id} style={{ padding: '14px 16px', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {(FIELD_TYPE_DEFS.find(d => d.type === field.type) || FIELD_TYPE_DEFS[0]).icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
                      {field.label}
                      {field.required && <span style={{ marginLeft: 6, fontSize: 10, color: '#ef4444', background: 'rgba(239,68,68,0.12)', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>Required</span>}
                    </div>
                    <div style={{ fontSize: 11, color: '#475569' }}>
                      <span style={{ fontWeight: 600, color: '#64748b' }}>{(FIELD_TYPE_DEFS.find(d => d.type === field.type) || { label: field.type }).label}</span>
                      {' — '}{(FIELD_TYPE_DEFS.find(d => d.type === field.type) || { desc: '' }).desc}
                      {field.options?.length > 0 && <span style={{ color: '#7c3aed' }}> · {field.options.join(', ')}</span>}
                    </div>
                  </div>
                  <button onClick={() => deleteCustomField(field.id)}
                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                    ✕ Delete
                  </button>
                </div>
              ))}
            </div>

            {showAddField && (
              <div style={{ marginTop: 16, padding: '20px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>➕ New Custom Field</div>

                {/* Field label */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Field Label *</label>
                  <input className="crm-input" placeholder="e.g. Target University, Test Score, Visa Status" value={fieldForm.label} onChange={e => setFieldForm(p => ({ ...p, label: e.target.value }))} style={{ maxWidth: 380 }} />
                </div>

                {/* Field type picker — visual cards */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Field Type — choose what kind of input this creates</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                    {FIELD_TYPE_DEFS.map(def => (
                      <div key={def.type}
                        onClick={() => setFieldForm(p => ({ ...p, type: def.type }))}
                        style={{
                          padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                          border: fieldForm.type === def.type ? '2px solid #3b82f6' : '1.5px solid rgba(0,0,0,0.08)',
                          background: fieldForm.type === def.type ? 'rgba(59,130,246,0.08)' : '#fff',
                          transition: 'all 0.12s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          <span style={{ fontSize: 16 }}>{def.icon}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: fieldForm.type === def.type ? '#2563eb' : '#0f172a' }}>{def.label}</span>
                          {fieldForm.type === def.type && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: '#2563eb' }}>✓</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>{def.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Options for select type */}
                {fieldForm.type === 'select' && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Dropdown Options <span style={{ color: '#64748b', fontWeight: 400 }}>(comma-separated)</span></label>
                    <input className="crm-input" placeholder="Option 1, Option 2, Option 3" value={fieldForm.options} onChange={e => setFieldForm(p => ({ ...p, options: e.target.value }))} />
                    {fieldForm.options && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                        {fieldForm.options.split(',').map(o => o.trim()).filter(Boolean).map((o, i) => (
                          <span key={i} style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(59,130,246,0.12)', color: '#2563eb', fontSize: 12, fontWeight: 500 }}>{o}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Required toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <input type="checkbox" className="crm-checkbox" id="req" checked={fieldForm.required} onChange={e => setFieldForm(p => ({ ...p, required: e.target.checked }))} />
                  <label htmlFor="req" style={{ fontSize: 13, color: '#475569', cursor: 'pointer' }}>Make this field required</label>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn-primary" onClick={handleAddField} disabled={!fieldForm.label.trim()}>Add Field</button>
                  <button className="btn-ghost" onClick={() => setShowAddField(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Team Members ────────────────────────────────────────────── */}
        {tab === 'team' && (
          <div>
            {/* User cap status bar */}
            {(() => {
              const cap = company?.userCap;
              if (!cap) return null;
              const activeCount = (users || []).filter(u => u.status !== 'removed').length;
              const pct = Math.min(100, Math.round((activeCount / cap) * 100));
              const atWarn  = pct >= 80 && pct < 100;
              const atLimit = pct >= 100;
              const barColor = atLimit ? '#ef4444' : atWarn ? '#f59e0b' : '#10b981';
              return (
                <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, border: `1px solid ${barColor}33`, background: `${barColor}0a` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>
                      {atLimit ? '🚫 User limit reached' : atWarn ? '⚠️ Approaching user limit' : '👥 Team capacity'}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>{activeCount} / {cap} seats used</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 99, width: `${pct}%`, background: barColor, transition: 'width 0.4s' }} />
                  </div>
                  {atLimit && (
                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 5 }}>
                      You've reached your team limit. Ask your super admin to increase the quota to add more members.
                    </div>
                  )}
                  {atWarn && !atLimit && (
                    <div style={{ fontSize: 11, color: '#92400e', marginTop: 5 }}>
                      You're close to your user limit. Contact your super admin to increase the quota.
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Team Members</h2>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Manage counsellors and their access</p>
              </div>
              {(() => {
                const cap = company?.userCap;
                const activeCount = (users || []).filter(u => u.status !== 'removed').length;
                const atLimit = cap && activeCount >= cap;
                return (
                  <button className="btn-primary" onClick={() => !atLimit && setShowAddUser(true)}
                    disabled={!!atLimit}
                    style={{ fontSize: 13, padding: '8px 16px', opacity: atLimit ? 0.5 : 1, cursor: atLimit ? 'not-allowed' : 'pointer' }}
                    title={atLimit ? `User limit of ${cap} reached. Contact super admin to increase quota.` : ''}>
                    + Add Member
                  </button>
                );
              })()}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {users.length === 0 && (
                <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No team members yet. Add your first counsellor below.
                </div>
              )}
              {users.map(user => {
                const safeName   = user.name   || user.email || 'Unknown';
                const safeAvatar = user.avatar || { bg: '#7c3aed', color: '#fff' };
                const initials   = safeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={user.id} style={{ padding: '14px 16px', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="avatar" style={{ background: safeAvatar.bg, color: safeAvatar.color, width: 40, height: 40, fontSize: 14 }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{safeName}</div>
                      <div style={{ fontSize: 12, color: '#475569', marginTop: 1 }}>{user.email || '—'}</div>
                    </div>
                    <span style={{
                      padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                      background: user.role === 'admin' ? 'rgba(239,68,68,0.15)' : user.role === 'manager' ? 'rgba(245,158,11,0.15)' : 'rgba(124,58,237,0.15)',
                      color: user.role === 'admin' ? '#f87171' : user.role === 'manager' ? '#fbbf24' : '#a78bfa',
                    }}>{user.role || 'counsellor'}</span>
                    <div style={{ fontSize: 12, color: '#64748b', minWidth: 70, textAlign: 'right' }}>{leadCountByUser[user.id] || 0} leads</div>
                    <button onClick={() => deleteUser(user.id)}
                      style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>

            {showAddUser && (
              <div style={{ marginTop: 16, padding: '16px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#34d399', marginBottom: 12 }}>Add New Member</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Full Name *</label>
                    <input className="crm-input" placeholder="Full name" value={userForm.name} onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Email *</label>
                    <input className="crm-input" placeholder="email@company.com" type="email" value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Role</label>
                    <select className="crm-select" style={{ width: '100%' }} value={userForm.role} onChange={e => setUserForm(p => ({ ...p, role: e.target.value }))}>
                      <option value="counsellor">Counsellor</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                      <option value="digital_media">Digital Media</option>
                      <option value="test_prep">Test Prep Teacher</option>
                    </select>
                    {userForm.role === 'test_prep' && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                        This role only sees the Test Prep page by default. Admins can grant additional pages via Access Control below.
                      </div>
                    )}
                    {userForm.role === 'digital_media' && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                        This role only ever sees the Digital Media page (lead quality, status, and notes for paid/digital channels) — it isn't part of the Access Control matrix below, and can't be widened from there.
                      </div>
                    )}
                  </div>
                </div>
                {inviteError && (
                  <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: 12, color: '#dc2626', marginBottom: 10 }}>
                    ⚠️ {inviteError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-primary" onClick={handleAddUser} disabled={inviting}>
                    {inviting ? 'Adding…' : 'Add Member'}
                  </button>
                  <button className="btn-ghost" onClick={() => { setShowAddUser(false); setInviteError(''); }}>Cancel</button>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                  💡 Share the app link with this person so they can sign up with this email.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Integrations ────────────────────────────────────────────── */}
        {tab === 'integrations' && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Integrations</h2>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Connect your lead sources and communication channels</p>
            </div>

            {/* Full integration management lives in the dedicated Integrations page —
                Meta Ads, WhatsApp/SMS, Email Sending, Google Ads, AI Calling, and Google
                Sheets all live there now, so this tab is just a signpost over rather than
                a second copy of that UI. */}
            <div style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.07),rgba(59,130,246,0.07))', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 14, padding: '28px 24px', display: 'flex', alignItems: 'center', gap: 18 }}>
              <div style={{ fontSize: 40 }}>🔗</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 5 }}>Full Integration Management</div>
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>Connect Meta Ads (Facebook &amp; Instagram), WhatsApp/SMS, Email Sending, Google Ads, AI Calling, and Google Sheets — manage OAuth flows, credentials, webhooks, and sync activity all from the dedicated Integrations section.</div>
              </div>
              <button className="btn-primary" onClick={() => setCurrentPage('integrations')}
                style={{ flexShrink: 0, padding: '10px 22px', fontSize: 13.5 }}>
                Open Integrations →
              </button>
            </div>
          </div>
        )}

        {/* ── Documents Drive ─────────────────────────────────────────── */}
        {tab === 'documents' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Documents Drive Setup</h2>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 4, lineHeight: 1.6 }}>
                Connect a shared Google Drive or OneDrive folder as the team's central document storage.
                The CRM will auto-create per-counselor and per-student folders inside it — no manual setup needed.
              </p>
            </div>

            {driveConfigLoading ? (
              <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>
            ) : (
              <>
                {/* Step 1 — Provider selector */}
                <div style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 12 }}>Step 1 — Choose Drive Provider</div>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 14 }}>
                    Select which cloud storage your team will use. Only providers connected in <strong>Email &amp; Calendar</strong> can be selected.
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {/* Google Drive option */}
                    {(() => {
                      const g = oauthStatus.google || {};
                      const isConnected = g.connected;
                      const isSelected  = selectedProvider === 'google';
                      return (
                        <button
                          onClick={() => { if (isConnected) { setSelectedProvider('google'); localStorage.setItem(`driveProviderDraft_${currentUser.companyId}`, 'google'); } }}
                          disabled={!isConnected}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '12px 18px', borderRadius: 10, cursor: isConnected ? 'pointer' : 'not-allowed',
                            border: isSelected ? '2px solid #ea4335' : '2px solid #e2e8f0',
                            background: isSelected ? 'rgba(234,67,53,0.07)' : '#fff',
                            opacity: isConnected ? 1 : 0.45, transition: 'all 0.15s', minWidth: 200,
                          }}>
                          <svg width="22" height="22" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                          </svg>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Google Drive</div>
                            <div style={{ fontSize: 11, color: isConnected ? '#10b981' : '#94a3b8' }}>
                              {isConnected ? `✅ ${g.email}` : 'Not connected'}
                            </div>
                          </div>
                          {isSelected && <span style={{ marginLeft: 'auto', fontSize: 16, color: '#ea4335' }}>✔</span>}
                        </button>
                      );
                    })()}
                    {/* OneDrive option */}
                    {(() => {
                      const m = oauthStatus.microsoft || {};
                      const isConnected = m.connected;
                      const isSelected  = selectedProvider === 'microsoft';
                      return (
                        <button
                          onClick={() => { if (isConnected) { setSelectedProvider('microsoft'); localStorage.setItem(`driveProviderDraft_${currentUser.companyId}`, 'microsoft'); } }}
                          disabled={!isConnected}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '12px 18px', borderRadius: 10, cursor: isConnected ? 'pointer' : 'not-allowed',
                            border: isSelected ? '2px solid #0078d4' : '2px solid #e2e8f0',
                            background: isSelected ? 'rgba(0,120,212,0.07)' : '#fff',
                            opacity: isConnected ? 1 : 0.45, transition: 'all 0.15s', minWidth: 200,
                          }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <path d="M11.5547 5.01953C9.37891 5.16797 7.42969 6.30469 6.25391 8.08984C5.89453 8.63281 5.625 9.25 5.48438 9.89453C4.01563 10.1797 2.77344 11.125 2.10547 12.4453C1.75 13.1406 1.57422 13.9141 1.57813 14.6875C1.58984 16.8828 3.37109 18.6641 5.56641 18.6875H17.7969C20.1719 18.6875 22.1016 16.7578 22.1016 14.3828C22.1016 12.2266 20.5625 10.4219 18.4844 10.1172C18.4609 7.32422 16.2031 5.01953 13.4141 5.01953C12.7891 5.01953 12.168 5.13672 11.5547 5.01953Z" fill="#0078D4"/>
                          </svg>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>OneDrive / SharePoint</div>
                            <div style={{ fontSize: 11, color: isConnected ? '#10b981' : '#94a3b8' }}>
                              {isConnected ? `✅ ${m.email}` : 'Not connected'}
                            </div>
                          </div>
                          {isSelected && <span style={{ marginLeft: 'auto', fontSize: 16, color: '#0078d4' }}>✔</span>}
                        </button>
                      );
                    })()}
                  </div>
                  {!oauthStatus.google?.connected && !oauthStatus.microsoft?.connected && (
                    <div style={{ marginTop: 12, fontSize: 12, color: '#ef4444' }}>
                      ⚠️ No drive account connected. Go to <strong>Email &amp; Calendar</strong> and connect Google or Microsoft first.
                    </div>
                  )}
                </div>

                {/* Step 2 — Base shared folder */}
                <div style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 10 }}>Step 2 — Shared Base Folder</div>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 12, lineHeight: 1.6 }}>
                    Create a shared folder in your Google Drive or OneDrive that all counselors have edit access to. Paste its URL below.
                    The CRM will create <strong>CounselorName → StudentName → {'{Essays, LORs, Photos, …}'}</strong> inside it automatically.
                  </div>
                  {driveConfig?.configured && (
                    <div style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: '#059669' }}>✅ Currently set: </span>
                      <a href={driveConfig.baseFolderUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{driveConfig.baseFolderName}</a>
                      <span style={{ color: '#64748b' }}> ({driveConfig.provider === 'google' ? 'Google Drive' : 'OneDrive'})</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="crm-input" value={driveFolderInput} onChange={e => setDriveFolderInput(e.target.value)}
                      placeholder="https://drive.google.com/drive/folders/…  or OneDrive share link"
                      style={{ flex: 1, fontSize: 13 }} />
                    <button onClick={handleSaveDriveConfig} disabled={driveSaving || !driveFolderInput.trim()}
                      style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 700, cursor: driveSaving ? 'wait' : 'pointer', opacity: driveSaving ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                      {driveSaving ? '⏳ Saving…' : '💾 Save'}
                    </button>
                  </div>
                  {driveSaveMsg && <div style={{ marginTop: 10, fontSize: 12, color: driveSaveMsg.startsWith('✅') ? '#059669' : '#ef4444' }}>{driveSaveMsg}</div>}
                </div>

                {/* Folder structure preview */}
                <div style={{ background: 'rgba(124,58,237,0.04)', borderRadius: 12, border: '1px dashed #c4b5fd', padding: '16px 20px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginBottom: 10 }}>📂 Auto-created folder structure</div>
                  <pre style={{ fontSize: 11, color: '#475569', margin: 0, lineHeight: 1.8, fontFamily: 'monospace' }}>{
`${driveConfig?.baseFolderName || 'Your Shared Folder'}/
├── Sagar Madan/              ← Counselor folder
│   ├── Rahul Sharma/         ← Student folder (auto-created on first access)
│   │   ├── Essays/
│   │   ├── LORs/
│   │   ├── Photographs/
│   │   ├── Form Copy/
│   │   ├── Reviews/
│   │   └── Documents/
│   └── Priya Patel/
└── Jane Doe/                 ← Another counselor`
                  }</pre>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Email & Calendar ────────────────────────────────────────── */}
        {tab === 'emailcal' && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Email & Calendar</h2>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Connect your own work email so your lead emails and calendar meetings sync with the CRM automatically</p>
            </div>

            {/* ── Auto-connect info banner ── */}
            {(loginGoogleEmail || loginMicrosoftEmail) ? (
              <div style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(59,130,246,0.06) 100%)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 22, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>✅</span>
                <div style={{ fontSize: 12, color: '#0f172a', lineHeight: 1.6 }}>
                  Your work account is <strong>automatically connected</strong> using the same account you signed into the CRM with. Email and calendar syncing is ready to use — no extra setup needed.
                </div>
              </div>
            ) : (
              <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(59,130,246,0.06) 100%)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 22, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>ℹ️</span>
                <div style={{ fontSize: 12, color: '#0f172a', lineHeight: 1.6 }}>
                  Connect your <strong>work email</strong> (Google Workspace or Microsoft 365) so your lead emails and calendar meetings sync automatically. Use your company email — e.g. <em>you@yourcompany.com</em>.
                </div>
              </div>
            )}

            {/* OAuth success banner */}
            {oauthSuccess && (
              <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#065f46' }}>
                <span style={{ fontSize: 18 }}>✅</span>
                <div>
                  <strong style={{ textTransform: 'capitalize' }}>{oauthSuccess}</strong> account connected successfully!
                  Your emails and calendar will now sync with the CRM.
                  <button onClick={() => setOauthSuccess('')} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', fontSize: 12, opacity: 0.7 }}>✕</button>
                </div>
              </div>
            )}

            {/* Work Account — auto-detected from CRM login, or manual connect */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 10 }}>Your Work Account</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>

              {/* ── Google card ── */}
              {(() => {
                const g = oauthStatus.google || {};
                // If user logged into CRM with Google, their work account IS that login.
                // Show it as auto-connected — no separate OAuth step needed.
                if (loginGoogleEmail && !g.connected) {
                  return (
                    <div className="glass-card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(234,67,53,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="24" height="24" viewBox="0 0 48 48">
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Google Workspace</div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{loginGoogleEmail} — signed in via Google</div>
                        <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 3, fontWeight: 500 }}>Auto-connected from your CRM login</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(16,185,129,0.2)', flexShrink: 0 }}>● Connected</span>
                    </div>
                  );
                }
                // Backend-connected (full API OAuth done) — show normally
                if (g.connected) {
                  const isPersonalGmail = g.email && g.email.endsWith('@gmail.com');
                  return (
                    <div>
                      <div className="glass-card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(234,67,53,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="24" height="24" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Google Workspace</div>
                          <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{g.email} — emails & calendar syncing</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(16,185,129,0.2)' }}>● Connected</span>
                          <button onClick={() => handleDisconnect('google')} disabled={oauthDisconnecting === 'google'}
                            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.07)', color: '#ef4444', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                            {oauthDisconnecting === 'google' ? 'Disconnecting…' : 'Disconnect'}
                          </button>
                        </div>
                      </div>
                      {isPersonalGmail && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '0 0 10px 10px', padding: '10px 16px', marginTop: -1, fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
                          <span style={{ flexShrink: 0, marginTop: 1 }}>⚠️</span>
                          <div><strong>This looks like a personal Gmail account.</strong> For work email and calendar sync, disconnect and reconnect with your <strong>company Google Workspace email</strong> (e.g. <em>you@yourcompany.com</em>).</div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Not logged in with Google and not connected — show connect button
                // (only relevant for users who signed in with email/password)
                if (!loginMicrosoftEmail) {
                  return (
                    <div className="glass-card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(234,67,53,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="24" height="24" viewBox="0 0 48 48">
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Google Workspace</div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>Connect your company Google Workspace email (you@yourcompany.com)</div>
                      </div>
                      {oauthLoading ? <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading…</span> : (
                        <button onClick={() => handleConnect('google')} disabled={oauthWaking === 'google'}
                          style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid rgba(234,67,53,0.3)', background: 'rgba(234,67,53,0.08)', color: '#ea4335', fontSize: 13, fontWeight: 500, cursor: oauthWaking === 'google' ? 'default' : 'pointer', flexShrink: 0, opacity: oauthWaking === 'google' ? 0.7 : 1 }}>
                          {oauthWaking === 'google' ? '⏳ Starting up…' : 'Connect Google'}
                        </button>
                      )}
                    </div>
                  );
                }
                return null; // Microsoft login user — Google card not needed
              })()}

              {/* ── Microsoft card ── */}
              {(() => {
                const m = oauthStatus.microsoft || {};
                // Auto-connected from CRM login
                if (loginMicrosoftEmail && !m.connected) {
                  return (
                    <div className="glass-card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(0,120,212,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="24" height="24" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Microsoft Outlook / 365</div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{loginMicrosoftEmail} — signed in via Microsoft</div>
                        <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 3, fontWeight: 500 }}>Auto-connected from your CRM login</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(16,185,129,0.2)', flexShrink: 0 }}>● Connected</span>
                    </div>
                  );
                }
                // Backend-connected (full API OAuth done)
                if (m.connected) {
                  return (
                    <div className="glass-card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(0,120,212,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="24" height="24" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Microsoft Outlook / 365</div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{m.email} — emails & calendar syncing</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(16,185,129,0.2)' }}>● Connected</span>
                        <button onClick={() => handleDisconnect('microsoft')} disabled={oauthDisconnecting === 'microsoft'}
                          style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.07)', color: '#ef4444', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                          {oauthDisconnecting === 'microsoft' ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      </div>
                    </div>
                  );
                }
                // Not logged in with Microsoft and not connected
                if (!loginGoogleEmail) {
                  return (
                    <div className="glass-card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(0,120,212,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="24" height="24" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Microsoft Outlook / 365</div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>Connect your company Outlook or Microsoft 365 account</div>
                      </div>
                      {oauthLoading ? <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading…</span> : (
                        <button onClick={() => handleConnect('microsoft')} disabled={oauthWaking === 'microsoft'}
                          style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid rgba(0,120,212,0.3)', background: 'rgba(0,120,212,0.08)', color: '#0078d4', fontSize: 13, fontWeight: 500, cursor: oauthWaking === 'microsoft' ? 'default' : 'pointer', flexShrink: 0, opacity: oauthWaking === 'microsoft' ? 0.7 : 1 }}>
                          {oauthWaking === 'microsoft' ? '⏳ Starting up…' : 'Connect Microsoft'}
                        </button>
                      )}
                    </div>
                  );
                }
                return null; // Google login user — Microsoft card not needed
              })()}
            </div>

            {/* Microsoft Teams — Admin consent helper */}
            <div style={{ background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.18)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#1d4ed8', marginBottom: 5, fontSize: 13 }}>⚠️ Team seeing "Need admin approval" for Microsoft / Teams?</div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.65, marginBottom: 10 }}>
                Your Microsoft 365 IT admin may have blocked third-party app access. The fix is simple — your IT admin visits a one-time consent URL and approves the app for the whole organization. After that, every team member can connect without the approval popup.
              </div>
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${BACKEND_URL}/api/oauth/microsoft/admin-consent-url?companyId=${currentUser?.companyId}`).then(r => r.json());
                    if (res.error) { alert('❌ ' + res.error); return; }
                    await navigator.clipboard.writeText(res.url).catch(() => {});
                    alert('✅ URL copied to clipboard!\n\nSend this to your Microsoft 365 admin. They open it in a browser, log in with their admin account, and click "Accept".\n\n' + res.url);
                  } catch (e) { alert('Failed to generate URL. Is the backend running?'); }
                }}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                📋 Copy Admin Consent URL for IT Admin
              </button>
            </div>

            {/* Need help banner */}
            <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#92400e' }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>🎧</span>
              <div style={{ lineHeight: 1.6 }}>
                <strong>Need help?</strong> Use the <strong>Help & Support</strong> button in the left sidebar to chat directly with platform support.
              </div>
            </div>

            {/* ── Outgoing Email (Notifications) ───────────────────────── */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12, marginTop: 4 }}>Outgoing Email — Notifications</div>
            <div className="glass-card" style={{ padding: 24, marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>SMTP Configuration</div>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 20 }}>
                Used to send email alerts to counselors when a student replies on WhatsApp. Works with Gmail, Outlook, or any SMTP provider.
                <span style={{ display: 'block', marginTop: 6, color: '#7c3aed', fontWeight: 500 }}>
                  Gmail tip: Enable 2-Step Verification → myaccount.google.com → search "App passwords" → generate one for "Clarion CRM"
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  { key: 'smtpHost',      label: 'SMTP Host',        ph: 'smtp.gmail.com',      type: 'text' },
                  { key: 'smtpPort',      label: 'SMTP Port',        ph: '587',                 type: 'text' },
                  { key: 'smtpUser',      label: 'Email Address',    ph: 'you@gmail.com',       type: 'text' },
                  { key: 'smtpPass',      label: 'Password / App Password', ph: 'xxxx xxxx xxxx xxxx', type: 'password' },
                  { key: 'smtpFromName',  label: 'From Name',        ph: 'Clarion CRM',         type: 'text' },
                  { key: 'smtpFromEmail', label: 'From Email',       ph: 'noreply@yourcompany.com', type: 'text' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 11, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 4 }}>{f.label}</label>
                    <input
                      className="crm-input"
                      type={f.type}
                      placeholder={f.ph}
                      style={{ height: 34, fontSize: 12, fontFamily: f.type === 'password' ? 'monospace' : 'inherit' }}
                      value={savedIntegrations?.smtp?.[f.key] || ''}
                      onChange={e => saveIntegrations({ smtp: { ...(savedIntegrations?.smtp || {}), [f.key]: e.target.value } })}
                    />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', fontSize: 12, color: '#065f46' }}>
                ✓ Settings save automatically as you type. The backend reads these credentials from your database — no server restart needed.
              </div>
            </div>

            {/* Setup note */}
            <div style={{ padding: '14px 18px', borderRadius: 12, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)', fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 600, color: '#7c3aed', marginBottom: 6 }}>🔧 Setup required</div>
              Email and calendar integrations require OAuth credentials from Google Cloud Console or Azure Active Directory. Click "Connect" above to open the respective developer console, enable the Gmail/Calendar/Outlook APIs, create OAuth 2.0 credentials, and paste the Client ID &amp; Secret in the Integrations tab.
            </div>
          </div>
        )}

        {/* ── Round Robin ─────────────────────────────────────────────── */}
        {tab === 'roundrobin' && (() => {
          const counselors = users.filter(u => u.role !== 'admin');
          const activeMode = rrSettings?.mode || 'round_robin';
          const rrPointer  = typeof rrSettings?.rrPointer === 'number' ? rrSettings.rrPointer : 0;

          // Build queue: IDs stored in rrSettings.queue, fall back to all counselors
          const storedQueue = rrSettings?.queue || [];
          const orderedQueue = storedQueue.length > 0
            ? storedQueue.map(id => counselors.find(u => u.id === id)).filter(Boolean)
            : [...counselors];
          // Add any counselors not yet in the queue
          counselors.forEach(u => { if (!orderedQueue.find(q => q.id === u.id)) orderedQueue.push(u); });

          const logVisibility = rrSettings?.logVisibility || [];

          const moveInQueue = (idx, dir) => {
            const newQ = [...orderedQueue];
            const swap = idx + dir;
            if (swap < 0 || swap >= newQ.length) return;
            [newQ[idx], newQ[swap]] = [newQ[swap], newQ[idx]];
            saveRRSettings({ queue: newQ.map(u => u.id) });
          };

          const toggleLogVisibility = (userId) => {
            const current = logVisibility.includes(userId)
              ? logVisibility.filter(id => id !== userId)
              : [...logVisibility, userId];
            saveRRSettings({ logVisibility: current });
          };

          // "Next up" for round_robin mode
          const nextUp = activeMode === 'round_robin' && orderedQueue.length > 0
            ? orderedQueue[rrPointer % orderedQueue.length]
            : null;

          return (
            <div>
              <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Round Robin Assignment</h2>
                <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Configure how new leads are distributed to your team</p>
              </div>

              {/* Mode selector */}
              <div className="glass-card" style={{ padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 16 }}>Assignment Mode</div>
                {[
                  { id: 'round_robin',   label: 'Round Robin',   desc: 'Leads distributed equally in rotation order',   icon: '🔄' },
                  { id: 'load_balanced', label: 'Load Balanced', desc: 'Assign to counsellor with fewest active leads', icon: '⚖️' },
                  { id: 'manual',        label: 'Manual',        desc: 'Admin manually assigns each lead',              icon: '👆' },
                ].map((mode) => {
                  const isActive = activeMode === mode.id;
                  return (
                    <div
                      key={mode.id}
                      onClick={() => saveRRSettings({ mode: mode.id })}
                      style={{ padding: '12px 14px', borderRadius: 10, marginBottom: 8, cursor: 'pointer',
                        border: '1px solid ' + (isActive ? '#7c3aed60' : 'rgba(0,0,0,0.06)'),
                        background: isActive ? 'rgba(124,58,237,0.1)' : 'rgba(0,0,0,0.03)',
                        display: 'flex', alignItems: 'center', gap: 12,
                        transition: 'all 0.15s' }}
                    >
                      <span style={{ fontSize: 20 }}>{mode.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isActive ? '#a78bfa' : '#0f172a' }}>{mode.label}</div>
                        <div style={{ fontSize: 12, color: '#475569' }}>{mode.desc}</div>
                      </div>
                      {isActive && <span style={{ fontSize: 11, color: '#7c3aed', background: 'rgba(124,58,237,0.2)', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Active</span>}
                    </div>
                  );
                })}
              </div>

              {/* Assignment Queue */}
              <div className="glass-card" style={{ padding: 24, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                    {activeMode === 'round_robin' ? 'Rotation Queue' : activeMode === 'load_balanced' ? 'Active Counsellors' : 'Counsellor Roster'}
                  </div>
                  {nextUp && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', padding: '4px 10px', borderRadius: 20 }}>
                      <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>NEXT UP</span>
                      <span style={{ fontSize: 12, color: '#065f46', fontWeight: 600 }}>{nextUp.name || nextUp.email}</span>
                    </div>
                  )}
                </div>
                {orderedQueue.length === 0 && (
                  <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '16px 0' }}>No counsellors added yet.</div>
                )}
                {orderedQueue.map((user, i) => {
                  const safeName   = user.name   || user.email || 'Unknown';
                  const safeAvatar = user.avatar || { bg: '#7c3aed', color: '#fff' };
                  const isNext = nextUp && nextUp.id === user.id;
                  return (
                    <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.05)',
                      background: isNext ? 'rgba(16,185,129,0.04)' : 'transparent', borderRadius: isNext ? 8 : 0 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8', width: 18, textAlign: 'right' }}>{i + 1}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <button onClick={() => moveInQueue(i, -1)} disabled={i === 0}
                          style={{ background: 'none', border: 'none', cursor: i === 0 ? 'not-allowed' : 'pointer', padding: '0 2px', color: i === 0 ? '#cbd5e1' : '#64748b', lineHeight: 1, fontSize: 10 }}>▲</button>
                        <button onClick={() => moveInQueue(i, 1)} disabled={i === orderedQueue.length - 1}
                          style={{ background: 'none', border: 'none', cursor: i === orderedQueue.length - 1 ? 'not-allowed' : 'pointer', padding: '0 2px', color: i === orderedQueue.length - 1 ? '#cbd5e1' : '#64748b', lineHeight: 1, fontSize: 10 }}>▼</button>
                      </div>
                      <div className="avatar" style={{ background: safeAvatar.bg, color: safeAvatar.color, width: 30, height: 30, fontSize: 11 }}>
                        {safeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isNext ? '#065f46' : '#0f172a' }}>{safeName}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{leadCountByUser[user.id] || 0} leads assigned</div>
                      </div>
                      {isNext && activeMode === 'round_robin' && (
                        <span style={{ fontSize: 10, background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '2px 7px', borderRadius: 20, fontWeight: 700 }}>Next</span>
                      )}
                      {activeMode === 'load_balanced' && (
                        <div style={{ display: 'flex', gap: 1 }}>
                          {Array.from({ length: Math.min(leadCountByUser[user.id] || 0, 5) }).map((_, j) => (
                            <div key={j} style={{ width: 6, height: 14, borderRadius: 3, background: '#3b82f6' }} />
                          ))}
                          {(leadCountByUser[user.id] || 0) === 0 && <span style={{ fontSize: 11, color: '#10b981' }}>Free</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Log Visibility */}
              <div className="glass-card" style={{ padding: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Activity Log Visibility</div>
                <div style={{ fontSize: 12, color: '#475569', marginBottom: 16 }}>Select which counsellors can view the Activity Log. Admins always have access.</div>
                {orderedQueue.length === 0 && (
                  <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '16px 0' }}>No counsellors to configure.</div>
                )}
                {orderedQueue.map((user) => {
                  const safeName   = user.name   || user.email || 'Unknown';
                  const safeAvatar = user.avatar || { bg: '#7c3aed', color: '#fff' };
                  const canView    = logVisibility.includes(user.id);
                  return (
                    <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                      <div className="avatar" style={{ background: safeAvatar.bg, color: safeAvatar.color, width: 30, height: 30, fontSize: 11 }}>
                        {safeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <span style={{ flex: 1, fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{safeName}</span>
                      <span style={{ fontSize: 11, color: '#64748b', marginRight: 8 }}>Can view logs</span>
                      <div onClick={() => toggleLogVisibility(user.id)}
                        style={{ width: 38, height: 21, borderRadius: 11, cursor: 'pointer',
                          background: canView ? '#7c3aed' : 'rgba(0,0,0,0.08)', position: 'relative', transition: 'background 0.2s' }}>
                        <div style={{ width: 15, height: 15, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3,
                          left: canView ? 20 : 3, transition: 'left 0.2s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── Branding ────────────────────────────────────────────────── */}
        {tab === 'branding' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Branding</h2>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Customise the look and feel of your CRM for your company.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div className="glass-card" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>App Name</div>
                <input className="crm-input" value={brandForm.appName} onChange={e => setBrandForm(f => ({ ...f, appName: e.target.value }))} placeholder="e.g. MyCompany CRM" style={{ width: '100%' }} />
              </div>
              <div className="glass-card" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Logo URL</div>
                <input className="crm-input" value={brandForm.logoUrl} onChange={e => setBrandForm(f => ({ ...f, logoUrl: e.target.value }))} placeholder="https://example.com/logo.png" style={{ width: '100%' }} />
                {brandForm.logoUrl && (
                  <img src={brandForm.logoUrl} alt="logo preview" style={{ marginTop: 10, height: 40, objectFit: 'contain', borderRadius: 6, border: '1px solid rgba(0,0,0,0.07)' }} />
                )}
              </div>
              <div className="glass-card" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Primary Color</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input type="color" value={brandForm.primary} onChange={e => setBrandForm(f => ({ ...f, primary: e.target.value }))} style={{ width: 48, height: 40, borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', padding: 2 }} />
                  <input className="crm-input" value={brandForm.primary} onChange={e => setBrandForm(f => ({ ...f, primary: e.target.value }))} style={{ width: 120 }} placeholder="#7c3aed" />
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: brandForm.primary, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0 }} />
                </div>
              </div>
              <div className="glass-card" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Secondary / Accent Color</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input type="color" value={brandForm.secondary} onChange={e => setBrandForm(f => ({ ...f, secondary: e.target.value }))} style={{ width: 48, height: 40, borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', padding: 2 }} />
                  <input className="crm-input" value={brandForm.secondary} onChange={e => setBrandForm(f => ({ ...f, secondary: e.target.value }))} style={{ width: 120 }} placeholder="#3b82f6" />
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: brandForm.secondary, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0 }} />
                </div>
              </div>
            </div>

            <div className="glass-card" style={{ padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 14 }}>Live Preview</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: 'linear-gradient(135deg,' + brandForm.primary + ',' + brandForm.secondary + ')',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 800, fontSize: 18,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                }}>
                  {(brandForm.appName || 'C').slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{brandForm.appName || 'My CRM'}</div>
                  <div style={{ fontSize: 11, color: '#475569' }}>PRO</div>
                </div>
                <div style={{
                  padding: '8px 18px', borderRadius: 8,
                  background: 'linear-gradient(135deg,' + brandForm.primary + ',' + brandForm.secondary + ')',
                  color: '#fff', fontWeight: 600, fontSize: 13, marginLeft: 'auto',
                }}>Sample Button</div>
              </div>
            </div>

            {brandSaved && (
              <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 9, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#059669', fontSize: 13, fontWeight: 600 }}>
                ✓ Branding saved — the sidebar logo and colours will update on next reload.
              </div>
            )}
            <button onClick={handleSaveBranding} disabled={brandSaving} className="btn-primary" style={{ minWidth: 160 }}>
              {brandSaving ? 'Saving…' : 'Save Branding'}
            </button>
          </div>
        )}

        {/* ── Access Control ───────────────────────────────────────────── */}
        {tab === 'access' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Access Control</h2>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
                Set default page access per role, then fine-tune for individual counsellors.
                Admins always have full access.
              </p>
            </div>

            {/* ── Role defaults ── */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Role Defaults</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {ROLES.map(role => {
                  const rolePages   = accessControl[role] || ALL_PAGES.map(p => p.id);
                  const isAdminRole = role === 'admin';
                  return (
                    <div key={role} className="glass-card" style={{ padding: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                        <span style={{
                          padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'capitalize',
                          background: isAdminRole ? 'rgba(124,58,237,0.12)' : 'rgba(59,130,246,0.12)',
                          color: isAdminRole ? '#7c3aed' : '#3b82f6',
                        }}>{role}</span>
                        {isAdminRole
                          ? <span style={{ fontSize: 11, color: '#94a3b8' }}>Full access (locked)</span>
                          : <span style={{ fontSize: 11, color: '#94a3b8' }}>{rolePages.length}/{ALL_PAGES.length} pages enabled</span>
                        }
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {ALL_PAGES.map(page => {
                          const allowed = isAdminRole ? true : rolePages.includes(page.id);
                          return (
                            <div key={page.id}
                              onClick={() => !isAdminRole && togglePageAccess(role, page.id)}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 12px', borderRadius: 8, cursor: isAdminRole ? 'default' : 'pointer',
                                background: allowed ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.04)',
                                border: '1px solid ' + (allowed ? 'rgba(16,185,129,0.18)' : 'rgba(239,68,68,0.12)'),
                                opacity: isAdminRole ? 0.75 : 1, transition: 'all 0.12s',
                              }}
                            >
                              <span style={{ fontSize: 13, color: '#0f172a' }}>{page.label}</span>
                              <span style={{ fontSize: 14 }}>{allowed ? '✅' : '🚫'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Per-user overrides ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Per-Counsellor Overrides</div>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
                Override role defaults for specific counsellors — grant extra pages or restrict individual ones.
              </p>
              {users.filter(u => u.role === 'counsellor' || u.role === 'counselor').length === 0 && (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '16px', textAlign: 'center', border: '1px dashed rgba(0,0,0,0.1)', borderRadius: 10 }}>
                  No counsellors in your team yet.
                </div>
              )}
              {users.filter(u => u.role === 'counsellor' || u.role === 'counselor').map(user => {
                const roleDefault  = accessControl['counsellor'] || ALL_PAGES.map(p => p.id);
                const userOverride = accessControl?.users?.[user.id] || { allow: [], deny: [] };
                return (
                  <div key={user.id} className="glass-card" style={{ padding: 16, marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: user.avatar?.bg || '#7c3aed', color: user.avatar?.color || '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                        {(user.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{user.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{user.email}</div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
                      {ALL_PAGES.map(page => {
                        const byRole    = roleDefault.includes(page.id);
                        const isDenied  = userOverride.deny?.includes(page.id);
                        const isGranted = userOverride.allow?.includes(page.id);
                        // Effective access: role gives it → check deny; role doesn't → check allow
                        const effective = isDenied ? false : isGranted ? true : byRole;
                        const hasOverride = isDenied || isGranted;
                        return (
                          <div key={page.id}
                            onClick={() => toggleUserPageAccess(user.id, page.id, effective)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '7px 10px', borderRadius: 8, cursor: 'pointer', transition: 'all 0.12s',
                              background: effective ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.04)',
                              border: '1px solid ' + (effective ? 'rgba(16,185,129,0.18)' : 'rgba(239,68,68,0.12)'),
                            }}
                          >
                            <span style={{ fontSize: 12, color: '#0f172a' }}>{page.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {hasOverride && (
                                <span style={{ fontSize: 9, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', borderRadius: 4, padding: '1px 4px' }}>OVERRIDE</span>
                              )}
                              <span style={{ fontSize: 13 }}>{effective ? '✅' : '🚫'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {(userOverride.allow?.length > 0 || userOverride.deny?.length > 0) && (
                      <button
                        onClick={() => clearUserOverrides(user.id)}
                        style={{ marginTop: 10, fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                      >
                        Reset to role defaults
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {accessSaved && (
              <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 9, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#059669', fontSize: 13, fontWeight: 600 }}>
                ✓ Access control saved — changes take effect immediately.
              </div>
            )}
            {/* ── Data Scope ─────────────────────────────────────────── */}
            <div className="glass-card" style={{ padding: 20, marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>📊 Counsellor Data Scope</div>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                Control what data counsellors can see in Leads and Reports.
              </p>

              {/* Leads scope */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Leads View</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[{ val: 'own', label: '🔒 Own Leads Only', desc: 'Counsellors see only leads assigned to them' },
                    { val: 'all', label: '👥 All Leads',      desc: 'Counsellors can see all leads in the CRM' }].map(opt => {
                    const current = (company?.dataScope?.counsellorLeads) || 'own';
                    const active = current === opt.val;
                    return (
                      <div key={opt.val} onClick={async () => {
                        await updateCompany({ dataScope: { ...(company?.dataScope || {}), counsellorLeads: opt.val } });
                      }} style={{
                        flex: 1, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                        border: active ? '2px solid #7c3aed' : '1px solid rgba(0,0,0,0.1)',
                        background: active ? 'rgba(124,58,237,0.07)' : 'rgba(0,0,0,0.02)',
                        transition: 'all 0.15s',
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: active ? '#7c3aed' : '#0f172a' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Reports scope */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Reports View</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[{ val: 'own', label: '🔒 Own Reports Only', desc: 'Counsellors see reports based on their leads' },
                    { val: 'all', label: '👥 All Reports',      desc: 'Counsellors can see full CRM reports' }].map(opt => {
                    const current = (company?.dataScope?.counsellorReports) || 'own';
                    const active = current === opt.val;
                    return (
                      <div key={opt.val} onClick={async () => {
                        await updateCompany({ dataScope: { ...(company?.dataScope || {}), counsellorReports: opt.val } });
                      }} style={{
                        flex: 1, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                        border: active ? '2px solid #7c3aed' : '1px solid rgba(0,0,0,0.1)',
                        background: active ? 'rgba(124,58,237,0.07)' : 'rgba(0,0,0,0.02)',
                        transition: 'all 0.15s',
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: active ? '#7c3aed' : '#0f172a' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <button onClick={handleSaveAccess} className="btn-primary" style={{ minWidth: 180 }}>
              {accessSaving ? 'Saving…' : 'Save Access Control'}
            </button>
          </div>
        )}

        {/* ── General ──────────────────────────────────────────────────── */}
        {tab === 'general' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>General Settings</h2>
              <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Data management and system utilities.</p>
            </div>

            <div className="glass-card" style={{ padding: 24, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 8 }}>Reset Demo Data</div>
              <p style={{ fontSize: 13, color: '#475569', marginBottom: 14 }}>Restore the default sample leads, stages, and followups. This will not delete your real data.</p>
              <button className="btn-secondary" onClick={async () => { await restoreDefaults(); alert('Demo data restored'); }} style={{ fontSize: 13 }}>
                Restore Default Data
              </button>
            </div>

            <div className="glass-card" style={{ padding: 24 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 8 }}>Deduplicate Leads</div>
              <p style={{ fontSize: 13, color: '#475569', marginBottom: 14 }}>Scan and remove duplicate lead entries from your pipeline.</p>
              <button className="btn-secondary" onClick={async () => { await deduplicateData(); alert('Deduplication complete'); }} style={{ fontSize: 13 }}>
                Run Deduplication
              </button>
            </div>
          </div>
        )}

        {/* ── Sources ──────────────────────────────────────────────────── */}
        {tab === 'sources' && (
          <SourcesManager customSources={customSources} saveCustomSources={saveCustomSources} />
        )}

        {tab === 'whatsapp' && (
          <WhatsAppSettings currentUser={currentUser} />
        )}

        {tab === 'waai' && (
          <WhatsAppAISettings company={company} currentUser={currentUser} />
        )}

        {/* ── Priorities ─────────────────────────────────────────────── */}
        {tab === 'priorities' && (
          <PrioritiesManager currentUser={currentUser} />
        )}

        {/* ── Doc Types ──────────────────────────────────────────────── */}
        {tab === 'docTypes' && (
          <DocTypesManager currentUser={currentUser} />
        )}

      </div>
    </div>
  );
}

// ─── WhatsApp AI Bot Settings ─────────────────────────────────────────────────
const WA_PROVIDERS = [
  {
    id: 'meta',
    name: 'Meta Cloud API (Direct)',
    icon: '📘',
    badge: 'Free tier',
    badgeColor: '#10b981',
    desc: '1000 conversations/month free. Official Meta API — most reliable.',
    fields: [
      { key: 'phoneNumberId',  label: 'Phone Number ID',   placeholder: '123456789012345',       type: 'text' },
      { key: 'accessToken',    label: 'Permanent Token',   placeholder: 'EAAxxxxxxxxxxxxxxx',     type: 'password' },
      { key: 'verifyToken',    label: 'Webhook Verify Token', placeholder: 'my-secret-token',    type: 'text' },
      { key: 'wabaId',         label: 'WABA ID',           placeholder: '123456789',              type: 'text' },
    ],
    webhookPath: '/webhook/waai/meta',
    docs: 'https://developers.facebook.com/docs/whatsapp/cloud-api',
  },
  {
    id: 'wati',
    name: 'Wati',
    icon: '🟢',
    badge: 'Easiest setup',
    badgeColor: '#7c3aed',
    desc: 'Managed WhatsApp API. No Meta verification needed. Paid from day 1 (~$40/mo).',
    fields: [
      { key: 'apiEndpoint',    label: 'API Endpoint',      placeholder: 'https://live-mt-server.wati.io/xxxxx', type: 'text' },
      { key: 'accessToken',    label: 'API Token',         placeholder: 'your-wati-token',        type: 'password' },
    ],
    webhookPath: '/webhook/waai/wati',
    docs: 'https://docs.wati.io',
  },
  {
    id: 'interakt',
    name: 'Interakt',
    icon: '🔵',
    badge: 'India-focused',
    badgeColor: '#0ea5e9',
    desc: 'Popular in India. Hindi + English support. ~₹2,999/mo.',
    fields: [
      { key: 'apiKey',         label: 'API Key',           placeholder: 'your-interakt-api-key',  type: 'password' },
    ],
    webhookPath: '/webhook/waai/interakt',
    docs: 'https://developers.interakt.ai',
  },
  {
    id: '360dialog',
    name: '360dialog',
    icon: '🌐',
    badge: 'Global',
    badgeColor: '#f59e0b',
    desc: 'European hosting, GDPR compliant. ~€49/mo.',
    fields: [
      { key: 'apiKey',         label: 'API Key',           placeholder: 'your-360dialog-key',     type: 'password' },
      { key: 'namespace',      label: 'Namespace',         placeholder: 'your-template-namespace', type: 'text' },
    ],
    webhookPath: '/webhook/waai/360dialog',
    docs: 'https://docs.360dialog.com',
  },
  {
    id: 'twilio',
    name: 'Twilio WhatsApp',
    icon: '🔴',
    badge: 'Pay-per-message',
    badgeColor: '#64748b',
    desc: 'Developer-friendly. Sandbox for testing is free. ~$0.005/message.',
    fields: [
      { key: 'accountSid',     label: 'Account SID',       placeholder: 'ACxxxxxxxxxxxxxxxx',     type: 'text' },
      { key: 'authToken',      label: 'Auth Token',        placeholder: 'your-auth-token',         type: 'password' },
      { key: 'fromNumber',     label: 'WhatsApp Number',   placeholder: 'whatsapp:+14155238886',   type: 'text' },
    ],
    webhookPath: '/webhook/waai/twilio',
    docs: 'https://www.twilio.com/docs/whatsapp',
  },
];

const AI_PROVIDERS = [
  { id: 'anthropic', name: 'Claude (Anthropic)', desc: 'Best at nuanced conversations. Recommended.', badge: '⭐ Recommended' },
  { id: 'openai',    name: 'GPT-4o (OpenAI)',    desc: 'Fast, widely used.', badge: '' },
  { id: 'groq',      name: 'Groq (Llama 3)',     desc: 'Ultra-fast, cheapest.', badge: '⚡ Fastest' },
];

function WhatsAppAISettings({ company, currentUser }) {
  const backendBase = process.env.REACT_APP_BACKEND_URL || 'https://your-backend.com';
  const [platformCfg, setPlatformCfg] = useState(null); // superadmin-level AI config
  const [config, setConfig] = useState({
    enabled: false,
    waProvider: '',
    waCredentials: {},
    aiProvider: 'anthropic',
    aiApiKey: '',
    botName: '',
    botPersonality: '',
    greeting: '',
    faqs: [],        // [{ q, a }]
    qualifyQuestions: [],  // strings
    escalationTriggers: ['speak to someone', 'talk to human', 'call me', 'agent'],
    collectFields: ['name', 'phone', 'email', 'program', 'country', 'budget'],
    handoffMessage: '',
    testPhone: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [section, setSection] = useState('provider'); // provider | ai | bot | faqs | qualify | test
  const [testMsg, setTestMsg] = useState('');
  const [testReply, setTestReply] = useState('');
  const [testing, setTesting] = useState(false);
  const [newFaq, setNewFaq] = useState({ q: '', a: '' });
  const [newTrigger, setNewTrigger] = useState('');
  const [newQQ, setNewQQ] = useState('');
  const [showKeys, setShowKeys] = useState({});

  // Load superadmin platform config + company config
  useEffect(() => {
    import('firebase/firestore').then(({ doc, getDoc }) => {
      import('../firebase/config').then(({ db }) => {
        // Load platform-level AI config
        getDoc(doc(db, 'platformConfig', 'main')).then(snap => {
          if (snap.exists()) setPlatformCfg(snap.data());
        }).catch(() => {});
        // Load company bot config
        if (company?.id) {
          getDoc(doc(db, 'companies', company.id, 'waAiConfig', 'default')).then(snap => {
            if (snap.exists()) setConfig(c => ({ ...c, ...snap.data() }));
          });
        }
      });
    });
  }, [company?.id]);

  // Derived: does superadmin allow companies to use their own key?
  const canUseOwnKey = platformCfg?.aiAllowCompanyKey === true;
  const platformHasKey = !!(platformCfg?.aiApiKey);

  const save = async () => {
    if (!company?.id) return;
    setSaving(true);
    try {
      const { setDoc, doc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      await setDoc(doc(db, 'companies', company.id, 'waAiConfig', 'default'), {
        ...config, updatedAt: serverTimestamp(),
      });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { console.error(e); alert('Save failed'); }
    setSaving(false);
  };

  const selectedProvider = WA_PROVIDERS.find(p => p.id === config.waProvider);
  const webhookUrl = selectedProvider ? `${backendBase}${selectedProvider.webhookPath}/${company?.id || 'COMPANY_ID'}` : '';

  const runTest = async () => {
    if (!testMsg.trim()) return;
    setTesting(true);
    setTestReply('');
    try {
      const res = await fetch(`${backendBase}/api/waai/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: company?.id, message: testMsg }),
      });
      const data = await res.json();
      setTestReply(data.reply || data.error || 'No response');
    } catch (e) {
      setTestReply('Could not connect to backend. Make sure it is running.');
    }
    setTesting(false);
  };

  const SECTIONS = [
    { id: 'provider', label: '1. WA Provider' },
    // Only show AI Engine step if superadmin allows company keys OR no platform key set
    ...(!platformHasKey || canUseOwnKey ? [{ id: 'ai', label: '2. AI Engine' }] : []),
    { id: 'bot',      label: canUseOwnKey || !platformHasKey ? '3. Bot Identity' : '2. Bot Identity' },
    { id: 'faqs',     label: canUseOwnKey || !platformHasKey ? '4. FAQs & Answers' : '3. FAQs & Answers' },
    { id: 'qualify',  label: canUseOwnKey || !platformHasKey ? '5. Qualification' : '4. Qualification' },
    { id: 'test',     label: canUseOwnKey || !platformHasKey ? '6. Test Bot' : '5. Test Bot' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>🤖 WhatsApp AI Bot</h2>
          <p style={{ fontSize: 13, color: '#475569', marginTop: 4, margin: '4px 0 0' }}>
            Auto-reply to WhatsApp leads 24/7 — qualifies them and hands off to counselors when ready.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Enable toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Bot {config.enabled ? 'ON' : 'OFF'}</span>
            <div onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))} style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
              background: config.enabled ? '#10b981' : '#e2e8f0', position: 'relative',
            }}>
              <div style={{
                position: 'absolute', top: 3, left: config.enabled ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }} />
            </div>
          </div>
          <button onClick={save} disabled={saving} style={{
            padding: '9px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
            background: saved ? '#10b981' : '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 13,
          }}>{saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save All'}</button>
        </div>
      </div>

      {/* Section nav */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, borderBottom: section === s.id ? '2px solid #7c3aed' : '2px solid transparent',
            color: section === s.id ? '#7c3aed' : '#64748b', marginBottom: -1,
          }}>{s.label}</button>
        ))}
      </div>

      {/* ── 1. WA Provider ─────────────────────────────────────────────── */}
      {section === 'provider' && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Choose your WhatsApp provider</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, marginBottom: 28 }}>
            {WA_PROVIDERS.map(p => (
              <div key={p.id} onClick={() => setConfig(c => ({ ...c, waProvider: p.id, waCredentials: {} }))} style={{
                border: config.waProvider === p.id ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                borderRadius: 12, padding: 16, cursor: 'pointer',
                background: config.waProvider === p.id ? 'rgba(124,58,237,0.04)' : '#fff',
                transition: 'all 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 20 }}>{p.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', flex: 1 }}>{p.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: p.badgeColor + '18', color: p.badgeColor }}>{p.badge}</span>
                  {config.waProvider === p.id && <span style={{ color: '#7c3aed', fontWeight: 800 }}>✓</span>}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>{p.desc}</div>
                <a href={p.docs} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  style={{ fontSize: 11, color: '#7c3aed', marginTop: 6, display: 'inline-block' }}>
                  View docs →
                </a>
              </div>
            ))}
          </div>

          {/* Credential fields for selected provider */}
          {selectedProvider && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
                {selectedProvider.icon} {selectedProvider.name} — Credentials
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                {selectedProvider.fields.map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>{f.label}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={f.type === 'password' && !showKeys[f.key] ? 'password' : 'text'}
                        placeholder={f.placeholder}
                        value={config.waCredentials[f.key] || ''}
                        onChange={e => setConfig(c => ({ ...c, waCredentials: { ...c.waCredentials, [f.key]: e.target.value } }))}
                        style={{ width: '100%', padding: '9px 36px 9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', fontFamily: f.type === 'password' && !showKeys[f.key] ? 'monospace' : 'inherit' }}
                      />
                      {f.type === 'password' && (
                        <button onClick={() => setShowKeys(s => ({ ...s, [f.key]: !s[f.key] }))} style={{
                          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#94a3b8',
                        }}>{showKeys[f.key] ? '🙈' : '👁'}</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Webhook URL */}
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>📋 YOUR WEBHOOK URL — paste this in {selectedProvider.name}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{ flex: 1, fontSize: 12, color: '#0f172a', wordBreak: 'break-all', background: 'none' }}>{webhookUrl}</code>
                  <button onClick={() => { navigator.clipboard.writeText(webhookUrl); }} style={{
                    padding: '6px 12px', borderRadius: 7, border: '1px solid #e2e8f0', cursor: 'pointer',
                    background: '#fff', fontSize: 11, fontWeight: 600, color: '#7c3aed', flexShrink: 0,
                  }}>Copy</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Platform key banner — shown when superadmin controls key */}
      {platformHasKey && !canUseOwnKey && (
        <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, marginBottom: 20, fontSize: 12, color: '#065f46' }}>
          ✅ <strong>AI powered by your platform provider.</strong> The AI engine is managed centrally — no API key needed from you.
        </div>
      )}
      {platformHasKey && canUseOwnKey && (
        <div style={{ padding: '12px 16px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, marginBottom: 20, fontSize: 12, color: '#92400e' }}>
          🔑 Platform provides a default AI key. You can override it with your own in step 2.
        </div>
      )}

      {/* ── 2. AI Engine ───────────────────────────────────────────────── */}
      {section === 'ai' && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Choose the AI brain for your bot</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
            {AI_PROVIDERS.map(p => (
              <div key={p.id} onClick={() => setConfig(c => ({ ...c, aiProvider: p.id }))} style={{
                border: config.aiProvider === p.id ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                borderRadius: 12, padding: 16, cursor: 'pointer',
                background: config.aiProvider === p.id ? 'rgba(124,58,237,0.04)' : '#fff',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', flex: 1 }}>{p.name}</span>
                  {config.aiProvider === p.id && <span style={{ color: '#7c3aed', fontWeight: 800 }}>✓</span>}
                </div>
                {p.badge && <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>{p.badge}</div>}
                <div style={{ fontSize: 11, color: '#64748b' }}>{p.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>
              {AI_PROVIDERS.find(p => p.id === config.aiProvider)?.name || 'AI'} API Key
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showKeys.aiKey ? 'text' : 'password'}
                placeholder="sk-ant-... or sk-... or gsk_..."
                value={config.aiApiKey || ''}
                onChange={e => setConfig(c => ({ ...c, aiApiKey: e.target.value }))}
                style={{ width: '100%', padding: '10px 40px 10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }}
              />
              <button onClick={() => setShowKeys(s => ({ ...s, aiKey: !s.aiKey }))} style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#94a3b8',
              }}>{showKeys.aiKey ? '🙈' : '👁'}</button>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              Stored encrypted in your Firestore. Never shared with other companies.
            </div>
          </div>
        </div>
      )}

      {/* ── 3. Bot Identity ────────────────────────────────────────────── */}
      {section === 'bot' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Bot Personality</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>Bot Name (how it introduces itself)</label>
              <input value={config.botName || ''} onChange={e => setConfig(c => ({ ...c, botName: e.target.value }))}
                placeholder="e.g. Priya, Alex, Aryan"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>
                Personality & Tone <span style={{ color: '#94a3b8', fontWeight: 400 }}>(system prompt — plain English)</span>
              </label>
              <textarea value={config.botPersonality || ''} onChange={e => setConfig(c => ({ ...c, botPersonality: e.target.value }))}
                rows={6} placeholder="e.g. You are Priya, a friendly education counselor at CAE. You help students explore study abroad options. Be warm, concise, and professional. Never make promises you can't keep. Always respond in English unless the student writes in another language."
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
            </div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Messages</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>Greeting (first message when bot activates)</label>
              <textarea value={config.greeting || ''} onChange={e => setConfig(c => ({ ...c, greeting: e.target.value }))}
                rows={4} placeholder="Hi! 👋 I'm Priya from CAE. I'm here to help you explore study abroad options. Which country are you interested in?"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>Handoff Message (when connecting to counselor)</label>
              <textarea value={config.handoffMessage || ''} onChange={e => setConfig(c => ({ ...c, handoffMessage: e.target.value }))}
                rows={4} placeholder="Great! I'm connecting you with one of our senior counselors right now. They'll reach out to you within a few minutes. 😊"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
            </div>
          </div>
        </div>
      )}

      {/* ── 4. FAQs & Answers ──────────────────────────────────────────── */}
      {section === 'faqs' && (
        <div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Add questions your leads commonly ask. The bot uses these as its knowledge base — it won't hallucinate answers it doesn't know.
          </div>
          {/* Add FAQ */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Add FAQ</div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Question (what the student might ask)</label>
              <input value={newFaq.q} onChange={e => setNewFaq(f => ({ ...f, q: e.target.value }))}
                placeholder="e.g. What is the tuition fee for Canada?"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Answer</label>
              <textarea value={newFaq.a} onChange={e => setNewFaq(f => ({ ...f, a: e.target.value }))}
                placeholder="e.g. Tuition fees in Canada typically range from CAD 15,000 to CAD 35,000 per year depending on the university and program."
                rows={3} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <button onClick={() => {
              if (!newFaq.q.trim() || !newFaq.a.trim()) return;
              setConfig(c => ({ ...c, faqs: [...(c.faqs || []), { q: newFaq.q.trim(), a: newFaq.a.trim() }] }));
              setNewFaq({ q: '', a: '' });
            }} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12 }}>
              + Add FAQ
            </button>
          </div>
          {/* FAQ list */}
          {(config.faqs || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13, border: '1px dashed #e2e8f0', borderRadius: 12 }}>
              No FAQs yet. Add your first one above.
            </div>
          ) : (config.faqs || []).map((faq, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Q: {faq.q}</div>
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>A: {faq.a}</div>
                </div>
                <button onClick={() => setConfig(c => ({ ...c, faqs: c.faqs.filter((_, j) => j !== i) }))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 18, flexShrink: 0, lineHeight: 1 }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 5. Qualification ───────────────────────────────────────────── */}
      {section === 'qualify' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Qualification questions */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Qualification Questions</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>Bot asks these to profile the lead before handing off.</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={newQQ} onChange={e => setNewQQ(e.target.value)}
                placeholder="e.g. Which country do you want to study in?"
                style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <button onClick={() => { if (!newQQ.trim()) return; setConfig(c => ({ ...c, qualifyQuestions: [...(c.qualifyQuestions || []), newQQ.trim()] })); setNewQQ(''); }}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12 }}>+</button>
            </div>
            {(config.qualifyQuestions || []).map((q, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f8fafc', borderRadius: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 700, minWidth: 18 }}>{i + 1}.</span>
                <span style={{ flex: 1, fontSize: 12, color: '#0f172a' }}>{q}</span>
                <button onClick={() => setConfig(c => ({ ...c, qualifyQuestions: c.qualifyQuestions.filter((_, j) => j !== i) }))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16 }}>×</button>
              </div>
            ))}
          </div>

          {/* Escalation triggers */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Escalation Triggers</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>
              If the student says any of these, the bot hands off to a human counselor immediately.
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={newTrigger} onChange={e => setNewTrigger(e.target.value)}
                placeholder="e.g. speak to someone"
                style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <button onClick={() => { if (!newTrigger.trim()) return; setConfig(c => ({ ...c, escalationTriggers: [...(c.escalationTriggers || []), newTrigger.trim().toLowerCase()] })); setNewTrigger(''); }}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12 }}>+</button>
            </div>
            {(config.escalationTriggers || []).map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#fef3c7', borderRadius: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 14 }}>⚡</span>
                <span style={{ flex: 1, fontSize: 12, color: '#92400e' }}>"{t}"</span>
                <button onClick={() => setConfig(c => ({ ...c, escalationTriggers: c.escalationTriggers.filter((_, j) => j !== i) }))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16 }}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 6. Test Bot ────────────────────────────────────────────────── */}
      {section === 'test' && (
        <div style={{ maxWidth: 600 }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
            Test your bot configuration before going live. This sends your message through the actual AI with your settings.
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
            {/* Chat window */}
            <div style={{ padding: 20, minHeight: 200, background: '#f8fafc' }}>
              {testReply ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <div style={{ background: '#7c3aed', color: '#fff', borderRadius: '12px 12px 2px 12px', padding: '10px 14px', fontSize: 13, maxWidth: '80%' }}>
                      {testMsg}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px 12px 12px 2px', padding: '10px 14px', fontSize: 13, maxWidth: '80%', lineHeight: 1.5 }}>
                      {testReply}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, paddingTop: 60 }}>
                  Type a message below and hit Send to test your bot
                </div>
              )}
            </div>
            <div style={{ padding: 14, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10 }}>
              <input value={testMsg} onChange={e => setTestMsg(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && runTest()}
                placeholder="e.g. Hi, I'm interested in studying in Canada"
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
              <button onClick={runTest} disabled={testing || !testMsg.trim()} style={{
                padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 13,
                opacity: testing || !testMsg.trim() ? 0.6 : 1,
              }}>{testing ? '…' : 'Send'}</button>
            </div>
          </div>
          {!config.aiApiKey && (
            <div style={{ marginTop: 14, padding: '12px 16px', background: '#fef3c7', borderRadius: 10, fontSize: 12, color: '#92400e' }}>
              ⚠️ Add your AI API key in step 2 before testing.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
